<template>
  <v-content class="mt-0 pt-0">
  <v-container fluid>
    <pr-loading v-model="loading" />

    <v-snackbar v-model="showMsg" right top multi-line :color="msgType" :timeout="msgTimeout" >
      <div v-html="msgText"></div>
      <v-btn dark flat @click="showMsg = false">
        {{ $t('OK') }}
      </v-btn>
    </v-snackbar>

    <!-- Cancel confirm -->
    <v-dialog v-model="showConfirm" persistent max-width="290">
      <v-card>
        <v-card-title class="headline">{{ $t('Confirm') }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text>{{ $t('Please confirm that you want to cancel') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" flat @click="doCancel(true)" ><v-icon>check_circle_outline</v-icon>&nbsp;{{ $t('OK') }}</v-btn>
          <v-btn color="green darken-1" flat @click="doCancel(false)">{{ $t('Cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Print manifest options and confirm -->
    <v-dialog v-model="printOpts.show" persistent max-width="500px" transition="dialog-transition">
      <v-card>
        <v-card-title class="headline px-3">{{ $t('Print PDF options') }}</v-card-title>
        <v-card-text>
          <v-form ref="form">
          <v-container grid-list-xs>
            <v-layout wrap>
              <v-flex xs12>
                <v-switch :label="$t('Freight amount')" v-model="printOpts.price"></v-switch>
              </v-flex>
              <v-flex xs12>
                <v-switch :label="$t('Insurance amount')" v-model="printOpts.insurance"></v-switch>
              </v-flex>
            </v-layout>
          </v-container>
          </v-form>
        </v-card-text>
        <v-divider class="mt-2"></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click.native="printOpts.show = false">{{ $t('Cancel') }}</v-btn>
          <v-btn color="blue darken-1" flat @click.native="printManifest(); printOpts.show = false">
            <v-icon>print</v-icon>&nbsp;{{ $t('Print' ) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card>
      <v-card-title :class="!$vuetify.breakpoint.xsOnly && 'pa-4'">

        <v-tooltip bottom v-if="shipment.accepted">
          <v-icon slot="activator" color="blue" class="mx-3 mt-1">
            {{ shipment.collected ? 'local_shipping' : 'send' }}
          </v-icon>
          {{ shipment.collected ? $t('Collected') : $t('Accepted') }}
        </v-tooltip>

        <span class="headline mr-4 blue--text">{{ $t('Shipment') }}</span>

        <v-tooltip bottom>
          <v-text-field slot="activator" :value="shipment.manifest" readonly
            :label="$t('Manifest')"
            class="mx-2 title manifest-field" style="max-width: 15rem"
            :hint="$t('Manifest') + (shipment.manifest ? ' - ' + $t('Track') : '')"
            :disabled="!shipment.manifest"
            @click="showTracking(shipment.manifest)"
          ></v-text-field>
          <span v-if="shipment.manifest"><v-icon small color="white">search</v-icon><span>{{ $t('Track') }}</span></span>
          <span v-else>{{ $t('Manifest') }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-text-field slot="activator" v-model="shipment.reference"
            :label="$t('Reference')" class="mx-2"
            :hint="$t('Reference.tooltip')"
            prepend-inner-icon="bookmark_border"
          ></v-text-field>
          {{ $t('Reference.tooltip') }}
        </v-tooltip>
        <v-tooltip bottom>
          <v-text-field slot="activator"
          v-if="isVRequiredAmazon()"
          v-model="shipment.amazon_reference"
            :label="$t('Amazon Reference')" class="mx-2"
            :hint="$t('Amazon Reference.tooltip')"
            persistent-hint
            :rules="vRequiredAmazon"
            :error="!shipment.amazon_reference"
            prepend-inner-icon="store"
          ></v-text-field>
          {{ $t('Amazon Reference.tooltip') }}
        </v-tooltip>

        <v-btn style="display: none" v-shortkey="{s1: ['ctrl', '1'], s2: ['ctrl', '2'], s3: ['ctrl', '3'], s4: ['ctrl', '4'] }" @shortkey="gotoStep"></v-btn>
        <v-spacer></v-spacer>
        <v-select
         v-if="costCenters && costCenters.length > 0"
         style="max-width: 13em"
         class="mr-4"
         v-model="shipment.cost_center"
         :items="sortedCostCenters"
         :label="$t('Cost Centers')"
         prepend-icon="fa-layer-group"
        >
         <template v-slot:selection="{ item }">
         {{ item }}
         </template>
         <template v-slot:item="{ item }">
          <v-list-tile-content>
           <v-list-tile-title v-text="item"></v-list-tile-title>
          </v-list-tile-content>
         </template>
        </v-select>
        <!--<v-select v-if="!calculator" style="max-width: 10em" class="mr-4 text-truncate"
          v-model="shipmentType" :items="shipmentTypes"
          :label="$t('Shipment type')" prepend-icon="fa-drafting-compass"
          :append-icon="shipmentTypes.length == 1 ? '' : '$vuetify.icons.dropdown'"
          :readonly="shipmentTypes.length == 1"
        >
          <template v-slot:selection="{ item, selected }">
            <span>{{ $t(item) }}</span>
          </template>
          <template v-slot:item="{ item, tile }">
            <v-list-tile-content>
              <v-list-tile-title v-text="$t(item)"></v-list-tile-title>
            </v-list-tile-content>
          </template>
        </v-select>-->

        <v-btn-toggle v-if="!shipment.accepted" v-model="calculator" class="elevation-0 mr-4">
          <v-btn flat :value="true">
            <v-icon left>fa-calculator</v-icon>
            <span>{{ $t("Calculator") }}</span>
          </v-btn>
          <v-btn flat :value="false">
            <v-icon left>fa-dolly</v-icon>
            <span>{{ $t("Request") }}</span>
          </v-btn>
        </v-btn-toggle>

        <v-select v-if="!calculator" style="max-width: 10em" class="mr-4"
          v-model="screen" :items="screenModes"
          :label="$t('Screen mode')" :prepend-icon="'fa-align-justify' + (screen === 'TABBED' ? ' fa-rotate-90' : '')"
          @change="toggleScreenModes()" :disabled="screenModesData.disabled"
        >
          <template v-slot:selection="{ item, selected }">
            {{ $t('RequestScreenMode.' + item) }}
          </template>
          <template v-slot:item="{ item, tile }">
            <v-list-tile-content>
              <v-list-tile-title v-text="$t('RequestScreenMode.' + item)"></v-list-tile-title>
            </v-list-tile-content>
          </template>
        </v-select>

        <div class="mt-2" style="width: 100%">
          <tracking-progress v-if="showTrackingProgress" :currentStep="progressState"></tracking-progress>
        </div>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-container grid-list-md :class="$vuetify.breakpoint.xsOnly && 'pa-2'">
          <v-layout wrap>
            <v-flex xs12 v-if="errorDetails">
              <v-alert type="error" :value="errorDetails">
                {{ displayErrors() }}
              </v-alert>
            </v-flex>
            <v-flex xs12 v-if="calculator">
              <v-form ref="calculator_form">
                <v-container grid-list-xs :class="!$vuetify.breakpoint.xsOnly && 'px-4'" elevation-2>
                    <v-layout row wrap>
                        <v-flex xs12 sm6 md4 lg3>
                          <v-tooltip top>
                            <v-badge slot="activator" overlap>
                              <template v-slot:badge
                                v-if="prices.length > 0 &&
                                  (prices[0].components.DISCOUNT_COURIER_OFFICE_DOOR || prices[0].components.DISCOUNT_COURIER_OFFICE_OFFICE)"
                              >$</template>

                              <v-radio-group v-model="shipment.source.direct" row>
                                <v-tooltip bottom>
                                  <v-radio slot="activator" :label="$t('Door')" :value="true" ></v-radio>
                                  <span>{{ $t('Pickup from client\'s door') }}</span>
                                </v-tooltip>
                                &nbsp;
                                <v-tooltip bottom>
                                  <v-radio slot="activator" :label="$t('Office')" :value="false"></v-radio>
                                  <span>{{ $t('Pickup from Tranpress office') }}</span>
                                </v-tooltip>
                              </v-radio-group>

                            </v-badge>
                            <span      v-if="prices.length > 0 && prices[0].components.DISCOUNT_COURIER_OFFICE_DOOR"  >{{ $t('Price.DISCOUNT_COURIER_OFFICE_DOOR')   + ' ' + showAmount(prices[0].components.DISCOUNT_COURIER_OFFICE_DOOR) }}</span>
                            <span v-else-if="prices.length > 0 && prices[0].components.DISCOUNT_COURIER_OFFICE_OFFICE">{{ $t('Price.DISCOUNT_COURIER_OFFICE_OFFICE') + ' ' + showAmount(prices[0].components.DISCOUNT_COURIER_OFFICE_OFFICE) }}</span>
                            <span v-else>-</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md5 lg6 v-if="!shipment.source.direct">
                          <v-suggest-office :label="$t('Office')" v-model="shipment.source" :is-courier-shipment="isCourierShipment"></v-suggest-office>
                        </v-flex>
                        <v-flex xs12 sm6 md5 lg6 v-if="shipment.source.direct">
                          <v-suggest-settlement :label="$t('Settlement')" v-model="shipment.source.address" :rules="vRequireSourceCode" :errors="errors('source.address.settlement.name', 1)" :is-courier-shipment="isCourierShipment"></v-suggest-settlement>
                        </v-flex>
                        <v-flex xs4 sm4 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.source.tail_lift" :label="$t('Tail lift.short')" :disabled="!shipment.source.direct"></v-checkbox>
                            <span>{{ $t('Tail lift.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs4 sm4 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.source.extended_convey" :label="$t('Extended convey.short')"></v-checkbox>
                            <span>{{ $t('Extended convey.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs4 sm4 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.source.thc" :label="$t('Handling.short')"></v-checkbox>
                            <span>{{ $t('Handling.explained') }}</span>
                          </v-tooltip>
                        </v-flex>

                        <v-divider class="flex xs12 pa-0 hidden-md-and-up"></v-divider>

                        <!-- Receiver -->
                        <v-flex xs12 sm6 md4 lg3>
                          <v-radio-group v-model="shipment.target.direct" row>
                            <v-tooltip bottom>
                              <v-radio slot="activator" :label="$t('Door')" :value="true" ></v-radio>
                              <span>{{ $t('Delivery to client\'s door') }}</span>
                            </v-tooltip>
                            &nbsp;
                            <v-tooltip bottom>
                              <v-radio slot="activator" :label="$t('Office')" :value="false"></v-radio>
                              <span>{{ $t('Delivery to Tranpress office') }}</span>
                            </v-tooltip>
                          </v-radio-group>
                        </v-flex>
                        <v-flex xs12 sm6 md5 lg6 v-if="!shipment.target.direct">
                          <v-suggest-office :label="$t('Office')" v-model="shipment.target" :is-courier-shipment="isCourierShipment"></v-suggest-office>
                        </v-flex>
                        <v-flex xs12 sm6 md5 lg6 v-if="shipment.target.direct">
                          <v-suggest-settlement :label="$t('Settlement')" v-model="shipment.target.address" :rules="vRequireTargetCode" :errors="errors('target.address.settlement.name')" :is-courier-shipment="isCourierShipment"></v-suggest-settlement>
                        </v-flex>
                        <v-flex xs4 sm4 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.target.tail_lift" :label="$t('Tail lift.short')" :disabled="!shipment.target.direct"></v-checkbox>
                            <span>{{ $t('Tail lift.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs4 sm4 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.target.extended_convey" :label="$t('Extended convey.short')"></v-checkbox>
                            <span>{{ $t('Extended convey.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs4 sm4 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.target.thc" :label="$t('Handling.short')"></v-checkbox>
                            <span>{{ $t('Handling.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                      </v-layout>

                      <!-- Packages and options -->
                      <v-layout row wrap>
                        <v-flex xs12>
                          <v-container fluid grid-list-xs px-0>
                            <v-layout v-for="(item,idx) in shipment.contents" :key="idx"
                              row wrap class="px-2 mb-2 elevation-2"
                              style="position: relative; border-radius: 4px;"
                            >
                              <v-flex xs12 sm3 md2 lg1>
                                <v-text-field v-model="item.count" type="number" :label="$t('Count.short')" :outline="outlinePackage(idx)" @input="packageCountChanged(idx)" :error-messages="errors('parcelSets[' + idx + '].count', 3)" min="1"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm9 md4 lg2>
                                <v-package-selector v-model="item.packaging.code" :values="packList" :label="$t('Packaging')" :outline="outlinePackage(idx)" :accept-change="packaging => acceptAdrPackaging(packaging, item, idx)" @pallet="packageChanged($event, idx)"></v-package-selector>
                              </v-flex>
                              <v-flex xs12 sm6 md3 lg2>
                                <v-text-field v-model="item.weight" type="number" :label="$t('Total weight')"  :outline="outlinePackage(idx)" :error-messages="errors('parcelSets[' + idx + '].weight', 3)" @keyup="clearErrors('parcelSets[' + idx + '].')" min="0"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm6 md3 lg1>
                                <v-text-field :value="calcParcelWeight(item)" type="number" :label="$t('Parcel weight')" min="0" :disabled="true"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm3 md2 lg1>
                                <v-text-field :value="parcelDimsCalc(item.parcel_x, 'parcel_x')" @input="parcelDimsCalc($event, 'parcel_x', item)" :desabled="!item.dimXEditable" type="number" :label="$t('Parcel_X.short') + ', ' + units.parcelDims" :outline="outlinePackage(idx)" :error-messages="errors('parcelSets[' + idx + '].parcel_X', 3)" @keyup="clearErrors('parcelSets[' + idx + '].')" min="0"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm3 md2 lg1>
                                <v-text-field :value="parcelDimsCalc(item.parcel_y, 'parcel_y')" @input="parcelDimsCalc($event, 'parcel_y', item)" :disabled="!item.dimYEditable" type="number" :label="$t('Parcel_Y.short') + ', ' + units.parcelDims" :outline="outlinePackage(idx)" :error-messages="errors('parcelSets[' + idx + '].parcel_Y', 3)" @keyup="clearErrors('parcelSets[' + idx + '].')" min="0"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm3 md2 lg1>
                                <v-text-field :value="parcelDimsCalc(item.parcel_z, 'parcel_z')" @input="parcelDimsCalc($event, 'parcel_z', item)" :disabled="!item.dimZEditable" type="number" :label="$t('Parcel_Z.short') + ', ' + units.parcelDims"  :outline="outlinePackage(idx)" :error-messages="errors('parcelSets[' + idx + '].parcel_Z', 3)" @keyup="clearErrors('parcelSets[' + idx + '].')" min="0"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm3 md3 lg1>
                                <v-text-field :value="calculateVolume(item)" type="number" :label="$t('Volume') + ', m³'" :outline="outlinePackage(idx)" :error-messages="errors('parcelSets[' + idx + '].volume', 3)" @keyup="clearErrors('parcelSets[' + idx + '].')" min="0" :disabled="true"></v-text-field>
                              </v-flex>

                              <v-flex xs4 sm2 md2 class="text-xs-center">
                                <v-tooltip bottom>
                                  <v-checkbox slot="activator" v-model="item.single_layer" :labelX="$t('Single layer')" class="justify-center">
                                    <template slot="label"><v-icon>layers_clear</v-icon></template>
                                  </v-checkbox>
                                  <span>{{ $t("Single layer.explain") }}</span>
                                </v-tooltip>
                              </v-flex>

                              <v-flex xs2 sm1 md1 class="text-xs-right align-self-center pb-3">
                                <v-btn v-if="enablePackageRemoval" flat icon outline small color="error" @click="removePackage(idx)"><v-icon>remove</v-icon></v-btn>
                              </v-flex>
                              <v-btn v-if="idx == shipment.contents.length-1"
                                @click="addPackage()"
                                absolute bottom right fab icon small dark color="light-blue"
                              ><v-icon>add</v-icon></v-btn>
                            </v-layout>
                          </v-container>
                        </v-flex>
                      </v-layout>

                      <v-layout row wrap>
                        <!-- Service -->
                        <v-flex xs12 sm12 md4 lg5>
                          <v-service-selector v-model="shipment.service.type" :type="serviceType" :is-city="isCityServiceType" :allowed="userOpts.shipmentServicesAllowed" multi="false"></v-service-selector>
                        </v-flex>
                        <!-- Packaging -->
                        <v-flex xs12 sm6 md4 offset-md1 lg4 offset-lg1>
                          <v-tooltip bottom>
                            <v-select-enum slot="activator" v-model="shipment.packaging_handling" :items="packagingHandlingListAvailable" :label="$t('Packaging Handling')" type="PackagingHandling"></v-select-enum>
                            <span>{{ $t('Packaging Handling tooltip') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md3 lg2>
                          <v-checkbox v-model="shipment.packaging_stretching" :label="$t('Stretching')"></v-checkbox>
                        </v-flex>
                      </v-layout>

                      <v-divider class="flex xs12 pa-0 hidden-md-and-up" v-if="shipment.cash_on_delivery.cash_on_delivery"></v-divider>

                      <v-layout row wrap>
                        <!-- COD -->
                        <v-flex xs12 sm6 md3 xl2>
                          <v-tooltip bottom>
                            <v-badge overlap left bottom slot="activator" class="mb-4">
                              <template v-slot:badge v-if="prices.length > 0 && prices[0].components.CASH_ON_DELIVERY">$</template>
                              <v-checkbox v-model="shipment.cash_on_delivery.cash_on_delivery" :label="$t('Cash On Delivery')" hide-details></v-checkbox>
                            </v-badge>
                            <span>{{ $t('Cash On Delivery') }}
                              <span v-if="prices.length > 0 && prices[0].components.CASH_ON_DELIVERY"> (+{{ showAmount(prices[0].components.CASH_ON_DELIVERY) }})</span>
                            </span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md3 xl1>
                          <v-tooltip v-if="shipment.cash_on_delivery.cash_on_delivery" bottom>
                            <v-checkbox slot="activator" v-model="shipment.cash_on_delivery.postal" :label="$t('Postal')" :disabled="!userOpts.pmtOnCodEditable"></v-checkbox>
                            <span>{{ $t('Postal money transfer') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md3 xl2>
                          <v-tooltip v-if="shipment.cash_on_delivery.cash_on_delivery" bottom>
                            <v-select-enum slot="activator" v-model="shipment.cash_on_delivery.payment_type" :items="codPaymentList" :label="$t('COD Payment Type')" type="CodPaymentType"></v-select-enum>
                            <span>{{ $t('COD Payment Type tooltip') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md3 xl2>
                          <v-text-field v-if="shipment.cash_on_delivery.cash_on_delivery" v-model="shipment.cash_on_delivery.amount.amount" type="number" :label="$t('Cash On Delivery Amount')"
                            :prefix="currencySymbol(shipment.cash_on_delivery.amount, true)"
                            :suffix="currencySymbol(shipment.cash_on_delivery.amount, false)"
                            :error-messages="errors(['cash_on_delivery.amount.amount', 'cashFromRecipient'], 4)"
                            @keyup="clearErrors(['cash_on_delivery.amount.amount', 'cashFromRecipient'])"
                            :min="limits.cashOnDeliveryAmount.min"
                            :max="limits.cashOnDeliveryAmount.max"
                            :rules="[rules.numberFormat, rules.cashOnDeliveryAmount]"/>
                        </v-flex>
                        <v-divider class="flex xs12 pa-0 hidden-md-and-up" v-if="shipment.cash_on_delivery.cash_on_delivery"></v-divider>

                        <!-- Insurance -->
                        <v-flex xs12 sm6 md3 xl1 offset-xl1>
                          <v-tooltip bottom>
                            <v-badge overlap left bottom slot="activator" class="mb-4">
                              <template v-slot:badge v-if="prices.length > 0 && prices[0].components.INSURANCE">$</template>
                              <v-checkbox v-model="shipment.insurance.active" :label="$t('Insurance')" hide-details></v-checkbox>
                            </v-badge>
                            <span>{{ $t('Insurance') }}
                              <span v-if="prices.length > 0 && prices[0].components.INSURANCE"> (+{{ showAmount(prices[0].components.INSURANCE) }})</span>
                            </span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md3 xl1>
                          <v-checkbox v-if="shipment.insurance.active" v-model="shipment.insurance.fragile" :label="$t('Fragile')"></v-checkbox>
                        </v-flex>
                        <v-flex xs12 sm6 md3 xl1>
                          <v-checkbox v-if="shipment.insurance.active" v-model="shipment.insurance.used" :label="$t('Used')"></v-checkbox>
                        </v-flex>
                        <v-flex xs12 sm6 md3 xl1>
                          <v-text-field v-if="shipment.insurance.active" v-model="shipment.insurance.liability.amount" type="number" :label="$t('Liability Amount')"
                            :prefix="currencySymbol(shipment.insurance.liability, true)"
                            :suffix="currencySymbol(shipment.insurance.liability, false)"
                            :error-messages="errors(['insurance.liability.amount', 'insurance.liability'], 4)"
                            @keyup="clearErrors('insurance.liability')"
                            @focus.native="$event.target.select()"
                            :min="limits.insuranceAmount.min"
                            :max="limits.insuranceAmount.max"
                            :rules="[rules.numberFormat, rules.insuranceAmount]" />
                        </v-flex>
                      </v-layout>

                      <v-layout row wrap>
                        <!-- Test, Review on Delivery -->
                        <v-flex xs6 md3 xl2>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.test_on_delivery" :label="$t('Test')"/>
                            <span>{{ $t('Test On Delivery') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs6 md3 xl2>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.review_on_delivery" :label="$t('Review')"/>
                            <span>{{ $t('Review On Delivery') }}</span>
                          </v-tooltip>
                        </v-flex>

                        <!-- Connected shipments -->
                        <v-flex v-for="c in connectedShipments" :key="c" xs12 sm6 md3 xl2>
                          <v-tooltip bottom>
                            <v-badge overlap left bottom slot="activator" class="mb-4">
                              <template v-slot:badge
                                v-if="prices.length > 0 && (
                                  (c == 'return_pod' && prices[0].components.RETURN_POD)       ||
                                  (c == 'return_doc' && prices[0].components.RETURN_DOCS)      ||
                                  (c == 'return_pkg' && prices[0].components.RETURN_PACKAGING)
                                )"
                              >$</template>
                              <v-checkbox v-model="shipment[c]" :label="$t('ConnectedShipment.' + c)" @change="checkConnectedShipments(c)" hide-details/>
                            </v-badge>
                            <span>{{ $t('ConnectedShipment.' + c + '.tip') }}
                              <span v-if="prices.length > 0">
                                {{ c == 'return_pod' && prices[0].components.RETURN_POD       ? ' (+' + showAmount(prices[0].components.RETURN_POD) + ')' : '' }}
                                {{ c == 'return_doc' && prices[0].components.RETURN_DOCS      ? ' (+' + showAmount(prices[0].components.RETURN_DOCS) + ')' : '' }}
                                {{ c == 'return_pkg' && prices[0].components.RETURN_PACKAGING ? ' (+' + showAmount(prices[0].components.RETURN_PACKAGING) + ')' : '' }}
                              </span>
                            </span>
                          </v-tooltip>
                        </v-flex>

                    </v-layout>
                </v-container>
              </v-form>
            </v-flex>
            <v-flex xs12 v-if="!calculator">
              <div v-show="screen === 'TABBED'">

              <v-stepper non-linear v-model="step"
                :alt-labels="$vuetify.breakpoint.xsOnly" :class="$vuetify.breakpoint.name"
              >
                <v-stepper-header>
                  <v-stepper-step editable step="1" :complete="stepComplete(1)" :rules="[ () => stepRules(1) ]">
                    <v-tooltip bottom>
                      <span slot="activator">{{ $t('Source Partner') }}</span>
                      [Ctrl + 1]
                    </v-tooltip>
                  </v-stepper-step>
                  <v-divider></v-divider>

                  <v-stepper-step editable step="2" :complete="stepComplete(2)" :rules="[ () => stepRules(2) ]">
                    <v-tooltip bottom>
                      <span slot="activator">{{ $t('Target Partner') }}</span>
                      [Ctrl + 2]
                    </v-tooltip>
                  </v-stepper-step>
                  <v-divider></v-divider>

                  <v-stepper-step editable step="3" :complete="stepComplete(3)" :rules="[ () => stepRules(3) ]">
                    <v-tooltip bottom>
                     <span slot="activator">{{ $t('Packages') }}</span>
                     [Ctrl + 3]
                    </v-tooltip>
                  </v-stepper-step>
                  <v-divider></v-divider>

                  <v-stepper-step editable step="4" :complete="stepComplete(4)" :rules="[ () => stepRules(4) ]">
                  <v-tooltip bottom>
                    <span slot="activator">{{ $t('Services') }}</span>
                    [Ctrl + 4]
                  </v-tooltip>
                  </v-stepper-step>
                </v-stepper-header>

                <v-stepper-items :class="isShipmentReadonly ? 'readonly-overlay' : ''">
                  <v-stepper-content step="1" :class="$vuetify.breakpoint.xsOnly && 'pa-0'">
                    <v-form ref="form1">
                    <v-container fluid grid-list-xs>
                      <v-layout row wrap>
                        <v-flex xs12 sm6>
                          <v-suggest-contact :label="$t('Source Partner')" v-model="shipment.source"
                            :own="contactSuggestOwn.source" @own-changed="contactSuggestOwn.source = $event; storePreferences('contactSuggestOwn')"
                            :error-messages="errors('source.partner', 2)" @clear-errors="clearErrors('source.partner')" :rules="vRequiredContact"
                          ></v-suggest-contact>
                        </v-flex>
                        <v-flex xs11 sm5>
                          <v-text-field v-model="shipment.source.contact_person" :label="$t('ContactName')" append-icon="person"></v-text-field>
                        </v-flex>
                        <v-flex xs1 align-self-center class="text-xs-right mr-0"><!-- EGN icon: fingerprint|perm_identity -->
                          <v-menu v-model="personalNumber.panel"
                            :close-on-content-click="false"
                            left transition="scale-transition" origin="top right"
                          >
                            <template #activator="{ on: menu }">
                              <v-tooltip bottom>
                                <template #activator="{ on: tooltip }">
                                  <v-btn v-on="{ ...tooltip, ...menu }" icon @click="personalNumberSet" class="mr-0">
                                    <v-icon
                                      :color="!isPersonalNumberRules ? 'error' : 'grey darken-1'"
                                      :class="{'blink': !isPersonalNumberRules}"
                                    >fingerprint</v-icon>
                                  </v-btn>
                                </template>
                                <span>{{ $t("Personal number") }}</span>
                              </v-tooltip>
                            </template>
                            <v-card>
                              <v-card-text>
                                <v-text-field id="personalNumber"
                                  :label="$t('Personal number')"
                                  prepend-inner-icon="fingerprint"
                                  @keyup.enter="personalNumber.panel = false"
                                  v-model="shipment.source.personal_number"
                                  :rules="personalNumber.rules"
                                ></v-text-field>
                                  <!-- :error-messages="personalNumberErrorMessages" -->
                                  <!-- :rules="personalNumber.rules" replaced by :error-messages because of not triggering form validation -->
                                <div class="caption" :class="{'mt-2': !isPersonalNumberRules}">
                                  {{ $t("Personal number.alert") }}
                                </div>
                              </v-card-text>
                            </v-card>
                          </v-menu>
                        </v-flex>
                        <v-flex xs1 sm1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.source.call_required" />
                            <span>{{ $t('Call Required') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs11 sm5>
                          <v-text-field ref="sourcePhone" v-model="shipment.source.contact_phone" :label="$t('Phone')" append-icon="phone" :rules="sourcePhoneRules" type="tel" :hint="$t('Phone international hint')"></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6>
                          <v-text-field v-model="shipment.source.contact_email" :label="$t('Email')" append-icon="email"></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4 lg3>
                          <v-radio-group v-model="shipment.source.direct" row :disabled="!userOpts.sourceDoorEditable" v-if="!isComplexShipment">
                            <v-tooltip bottom>
                              <v-radio slot="activator" :label="$t('Door')" :value="true" ></v-radio>
                              <span>{{ $t('Pickup from client\'s door') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <v-radio slot="activator" :label="$t('Office')" :value="false"></v-radio>
                              <span>{{ $t('Pickup from Tranpress office') }}</span>
                            </v-tooltip>
                          </v-radio-group>
                          <v-suggest-office v-else-if="shipmentType !== 'COMPLEX'" :label="station.source.disabled ? $t('Serving office') : $t('Complex to')" v-model="shipment.source" :station="station.source" :is-courier-shipment="isCourierShipment"></v-suggest-office>
                        </v-flex>
                        <v-flex xs12 sm6 md8 lg9 v-if="!shipment.source.direct">
                          <v-suggest-office :label="$t('Office')" v-model="shipment.source" :disabled="isShipmentSourceOfficeDisabled" :is-courier-shipment="isCourierShipment"></v-suggest-office>
                        </v-flex>
                        <v-flex xs12 sm6 md4 lg3 v-if="shipment.source.direct">
                          <v-suggest-settlement :label="$t('Settlement')" v-model="shipment.source.address" :rules="vRequireSourceCode" :errors="errors('source.address.settlement.name', 1)" :is-courier-shipment="isCourierShipment"></v-suggest-settlement>
                        </v-flex>
                        <v-flex xs12 sm6 md4 lg2 v-if="shipment.source.direct">
                          <v-text-field v-model="shipment.source.address.complex.name" :label="$t('Complex')" :error-messages="errors('sourceAddress.complexNameFT', 1)" @keyup="clearErrors('sourceAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4 lg2 v-if="shipment.source.direct">
                          <v-text-field v-model="shipment.source.address.street.name" :label="$t('Street')" :error-messages="errors('sourceAddress.streetNameFT', 1)"  @keyup="clearErrors('sourceAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm3 md1 lg1 v-if="shipment.source.direct">
                          <v-text-field v-model="shipment.source.address.street_number" label="№" :error-messages="errors('sourceAddress.streetNumber', 1)" @keyup="clearErrors('sourceAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs6 sm3 md1 lg1 v-if="shipment.source.direct">
                          <v-text-field v-model="shipment.source.address.block" :label="$t('block')" :error-messages="errors('sourceAddress.blockNumber', 1)" @keyup="clearErrors('sourceAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs6 sm3 md1 lg1 v-if="shipment.source.direct">
                          <v-text-field v-model="shipment.source.address.entrance" :label="$t('entrance')" :error-messages="errors('sourceAddress.entrance', 1)" @keyup="clearErrors('sourceAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs6 sm3 md1 lg1 v-if="shipment.source.direct">
                          <v-text-field v-model="shipment.source.address.floor" :label="$t('floor')"></v-text-field>
                        </v-flex>
                        <v-flex xs6 sm3 md1 lg1 v-if="shipment.source.direct">
                          <v-text-field v-model="shipment.source.address.apartment" :label="$t('apartment')"></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm9 md3 lg9 v-if="shipment.source.direct">
                          <v-text-field v-model="shipment.source.address.additional" :label="$t('Address additional')" :error-messages="errors('sourceAddress.address.street', 1)" @keyup="clearErrors('sourceAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs6 sm3 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.source.tail_lift" :label="$t('Tail lift.short')" :disabled="!shipment.source.direct"></v-checkbox>
                            <span>{{ $t('Tail lift.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs6 sm3 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.source.extended_convey" :label="$t('Extended convey.short')"></v-checkbox>
                            <span>{{ $t('Extended convey.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs6 sm3 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.source.thc" :label="$t('Handling.short')"></v-checkbox>
                            <span>{{ $t('Handling.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm3 md3>
                          <v-tooltip bottom>
                            <v-select slot="activator" v-model="shipment.source.access" :items="accessList" :label="$t('Access')" :hint="$t('Access.' + `${shipment.source.access}` + '.explained')">
                                <template v-slot:selection="{ item, selected }">
                                    {{ $t('Access.' + item) }}
                                </template>
                                <template v-slot:item="{ item, tile }">
                                    <v-list-tile-content>
                                        <v-list-tile-title v-text="$t('Access.' + item)"></v-list-tile-title>
                                    </v-list-tile-content>
                                </template>
                            </v-select>
                            <span>{{ $t('Access.'+ shipment.source.access+'.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm12 md6>
                          <v-text-field v-model="shipment.source.instructions" :label="$t('Instructions')"></v-text-field>
                        </v-flex>
                      </v-layout>
                    </v-container>
                    </v-form>
                  </v-stepper-content>

                  <v-stepper-content step="2" :class="$vuetify.breakpoint.xsOnly && 'pa-0'"><!-- Receiver -->
                    <v-form ref="form2">
                    <v-container fluid grid-list-xs>
                      <v-layout row wrap>
                        <v-flex xs11 sm5>
                          <v-suggest-contact :label="$t('Target Partner')" v-model="shipment.target"
                            :own="contactSuggestOwn.target" @own-changed="contactSuggestOwn.target = $event; storePreferences('contactSuggestOwn')"
                            :error-messages="errors('target.partner', 2)" @clear-errors="clearErrors('target.partner')" :rules="vRequiredContact"
                             @amazon-reference="showAmazonReference = $event"
                          ></v-suggest-contact>
                        </v-flex>
                        <v-flex xs1 align-self-center>
                          <v-tooltip bottom>
                            <v-btn slot="activator" icon @click="processContact"><v-icon color="grey darken-1">group_add</v-icon></v-btn>
                            <span>{{ $t("Save/create contact") }}</span>
                          </v-tooltip>
                          <v-dialog v-model="contact.show" transition="dialog-transition" persistent lazy>
                            <v-card>
                              <v-card-text class="pa-0">
                                <contact id="0" v-model="contact.data" @close="processContactBack($event)"></contact>
                              </v-card-text>
                            </v-card>
                          </v-dialog>
                        </v-flex>
                        <v-flex xs12 sm6>
                          <v-text-field v-model="shipment.target.contact_person" :label="$t('ContactName')" append-icon="person"></v-text-field>
                        </v-flex>
                        <v-flex xs1 sm1>
                          <v-tooltip bottom class="shrink mr-2">
                            <v-checkbox slot="activator" v-model="shipment.target.call_required" class="shrink mr-2"/>
                            <span>{{ $t('Call Required') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs11 sm5>
                          <v-text-field ref="targetPhone" v-model="shipment.target.contact_phone" :label="$t('Phone')" append-icon="phone" :rules="targetPhoneRules" type="tel" :hint="$t('Phone international hint')"></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6>
                          <v-text-field v-model="shipment.target.contact_email" :label="$t('Email')" append-icon="email"></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4 lg3>
                          <v-radio-group v-model="shipment.target.direct" row :disabled="!userOpts.targetDoorEditable" v-if="!isComplexShipment">
                            <v-tooltip bottom>
                              <v-radio slot="activator" :label="$t('Door')"   :value="true" ></v-radio>
                              <span>{{ $t('Delivery to client\'s door') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <v-radio slot="activator" :label="$t('Office')" :value="false"></v-radio>
                              <span>{{ $t('Delivery to Tranpress office') }}</span>
                            </v-tooltip>
                          </v-radio-group>
                          <v-suggest-office v-else-if="shipmentType !== 'COMPLEX'" :label="station.target.disabled ? $t('Serving office') : $t('Complex from')" v-model="shipment.target" :station="station.target" :is-courier-shipment="isCourierShipment"></v-suggest-office>
                        </v-flex>
                        <v-flex xs12 sm6 md8 lg9 v-if="!shipment.target.direct">
                          <v-suggest-office :label="$t('Office')" v-model="shipment.target" :is-courier-shipment="isCourierShipment" :rules="vRequiredOffice"></v-suggest-office>
                        </v-flex>
                        <v-flex xs12 sm6 md4 lg3 v-if="shipment.target.direct">
                          <v-suggest-settlement :label="$t('Settlement')" v-model="shipment.target.address" :rules="vRequireTargetCode" :errors="errors('target.address.settlement.name')" :is-courier-shipment="isCourierShipment"></v-suggest-settlement>
                        </v-flex>
                        <v-flex xs12 sm6 md4 lg2 v-if="shipment.target.direct">
                          <v-text-field v-model="shipment.target.address.complex.name" :label="$t('Complex')" :error-messages="errors('targetAddress.complexNameFT', 2)" @keyup="clearErrors('targetAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 md4 lg2 v-if="shipment.target.direct">
                          <v-text-field v-model="shipment.target.address.street.name" :label="$t('Street')" :error-messages="errors('targetAddress.streetNameFT', 2)" @keyup="clearErrors('targetAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm3 md1 lg1 v-if="shipment.target.direct">
                          <v-text-field v-model="shipment.target.address.street_number" label="№" :error-messages="errors('targetAddress.streetNumber', 2)" @keyup="clearErrors('targetAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs6 sm3 md1 lg1 v-if="shipment.target.direct">
                          <v-text-field v-model="shipment.target.address.block" :label="$t('block')" :error-messages="errors('targetAddress.blockNumber', 2)" @keyup="clearErrors('targetAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs6 sm3 md1 lg1 v-if="shipment.target.direct">
                          <v-text-field v-model="shipment.target.address.entrance" :label="$t('entrance')" :error-messages="errors('targetAddress.entrance', 2)" @keyup="clearErrors('targetAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs6 sm3 md1 lg1 v-if="shipment.target.direct">
                          <v-text-field v-model="shipment.target.address.floor" :label="$t('floor')"></v-text-field>
                        </v-flex>
                        <v-flex xs6 sm3 md1 lg1 v-if="shipment.target.direct">
                          <v-text-field v-model="shipment.target.address.apartment" :label="$t('apartment')"></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm9 md3 lg9 v-if="shipment.target.direct">
                          <v-text-field v-model="shipment.target.address.additional" :label="$t('Address additional')" :error-messages="errors('targetAddress.address.street', 2)" @keyup="clearErrors('targetAddress.')"></v-text-field>
                        </v-flex>
                        <v-flex xs6 sm3 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.target.tail_lift" :label="$t('Tail lift.short')" :disabled="!shipment.target.direct"></v-checkbox>
                            <span>{{ $t('Tail lift.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs6 sm3 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.target.extended_convey" :label="$t('Extended convey.short')"></v-checkbox>
                            <span>{{ $t('Extended convey.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs6 sm3 md1>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.target.thc" :label="$t('Handling.short')"></v-checkbox>
                            <span>{{ $t('Handling.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm3 md3>
                          <v-tooltip bottom>
                            <v-select slot="activator" v-model="shipment.target.access" :items="accessList" :label="$t('Access')" :hint="$t('Access.' + `${shipment.source.access}` + '.explained')">
                                <template v-slot:selection="{ item, selected }">
                                    {{ $t('Access.' + item) }}
                                </template>
                                <template v-slot:item="{ item, tile }">
                                    <v-list-tile-content>
                                        <v-list-tile-title v-text="$t('Access.' + item)"></v-list-tile-title>
                                    </v-list-tile-content>
                                </template>
                            </v-select>
                            <span>{{ $t('Access.'+ shipment.target.access+'.explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm12 md6>
                          <v-text-field v-model="shipment.target.instructions" :label="$t('Instructions')"></v-text-field>
                        </v-flex>
                      </v-layout>
                    </v-container>
                    </v-form>
                  </v-stepper-content>

                  <v-stepper-content step="3" :class="$vuetify.breakpoint.xsOnly && 'pa-0'">
                    <v-form ref="form3">
                    <v-container fluid grid-list-xs>
                      <v-layout row wrap>
                        <v-flex xs12>
                          <v-text-field v-model="shipment.description" :label="$t('Description')" :rules="vRequiredDescription"></v-text-field>
                        </v-flex>
                        <v-flex xs9 sm11>
                          <span class="headline">{{ $t('Packages') }}</span>
                        </v-flex>
                        <v-flex xs3 sm1 class="text-xs-right">
                          <v-btn flat icon @click="addPackage()"><v-icon>add_circle_outline</v-icon></v-btn>
                        </v-flex>
                        <v-flex xs12>
                          <v-container fluid grid-list-xs class="ma-0 pa-0">
                            <v-layout v-for="(item, idx) in shipment.contents" :key="idx" row wrap :style="idx != 0 ? 'border-top: 1px solid rgba(0,0,0,.12)' : ''" style="position: relative;">
                              <v-flex xs12 sm3 md2 lg1>
                                <v-text-field v-model="item.count" type="number" :label="$t('Count.short')" :outline="outlinePackage(idx)" @input="packageCountChanged(idx)" :error-messages="errors('parcelSets[' + idx + '].count', 3)" min="1"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm9 md4 lg2>
                                <v-package-selector v-model="item.packaging.code" :values="packList" :label="$t('Packaging')" :outline="outlinePackage(idx)" :accept-change="packaging => acceptAdrPackaging(packaging, item, idx)" @pallet="packageChanged($event, idx)" :rules="vRequiredPackaging" :errors="errors('parcelSets[' + idx + '].code')"></v-package-selector>
                              </v-flex>
                              <v-flex xs12 sm6 md3 lg2>
                                <v-text-field v-model="item.weight" type="number" :label="$t('Total weight')" :outline="outlinePackage(idx)" :error-messages="errors('parcelSets[' + idx + '].weight', 3)" @keyup="clearErrors('parcelSets[' + idx + '].')" min="0" :rules="vRequiredNumber"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm6 md3 lg1>
                                <v-text-field :value="calcParcelWeight(item)" type="number" :label="$t('Parcel weight')" min="0" :disabled="true"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm3 md2 lg1>
                                <v-text-field :value="parcelDimsCalc(item.parcel_x, 'parcel_x')" @input="parcelDimsCalc($event, 'parcel_x', item)" type="number" :disabled="!item.dimXEditable" :label="$t('Parcel_X.short') + ', ' + units.parcelDims" :outline="outlinePackage(idx)" :error-messages="errors('parcelSets[' + idx + '].parcel_X', 3)" @keyup="clearErrors('parcelSets[' + idx + '].')" min="0"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm3 md2 lg1>
                                <v-text-field :value="parcelDimsCalc(item.parcel_y, 'parcel_y')" @input="parcelDimsCalc($event, 'parcel_y', item)" type="number" :disabled="!item.dimYEditable" :label="$t('Parcel_Y.short') + ', ' + units.parcelDims" :outline="outlinePackage(idx)" :error-messages="errors('parcelSets[' + idx + '].parcel_Y', 3)" @keyup="clearErrors('parcelSets[' + idx + '].')" min="0"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm3 md2 lg1>
                                <v-text-field :value="parcelDimsCalc(item.parcel_z, 'parcel_z')" @input="parcelDimsCalc($event, 'parcel_z', item)" type="number" :disabled="!item.dimZEditable" :label="$t('Parcel_Z.short') + ', ' + units.parcelDims" :outline="outlinePackage(idx)" :error-messages="errors('parcelSets[' + idx + '].parcel_Z', 3)" @keyup="clearErrors('parcelSets[' + idx + '].')" min="0"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm3 md3 lg1>
                                <v-text-field :value="calculateVolume(item)" type="number" :label="$t('Volume') + ', m³'" :outline="outlinePackage(idx)" :error-messages="errors('parcelSets[' + idx + '].volume', 3)" @keyup="clearErrors('parcelSets[' + idx + '].')" min="0" :disabled="true"></v-text-field>
                              </v-flex>
                              <v-flex xs12 sm3 md3 lg2 class="align-self-center text-xs-center pl-2">
                                <v-layout>
                                  <v-flex>

                                  <v-tooltip bottom>
                                    <v-btn slot="activator" icon small @click="item.single_layer = !item.single_layer" class="mx-0">
                                      <v-icon v-if="item.single_layer" color="blue darken-1">layers_clear</v-icon>
                                      <v-icon v-else color="grey darken-1">layers</v-icon>
                                    </v-btn>
                                    <span>{{ $t("Single layer.explain") }}: {{ $t(item.single_layer ? "On" : "Off") }}</span>
                                  </v-tooltip>

                                  </v-flex>
                                  <v-flex>

                                  <v-tooltip bottom>
                                    <v-btn slot="activator" icon small @click="openDangerousGoods(item)" :color="hasAdr(item) ? 'yellow' : 'grey--text text--darken-1'" class="mx-0">
                                      <v-icon size="21px">fa-biohazard</v-icon>
                                    </v-btn>
                                    <span>{{ $t("Dangerous goods") }}<span v-if="hasAdr(item)">: {{ item.dangerous_goods.length }} {{ $t('Count.short') }}</span></span>
                                  </v-tooltip>
                                  <div v-if="adrError(item, idx)" class="error--text caption" role="alert">{{ adrError(item, idx) }}</div>

                                  </v-flex>
                                </v-layout>
                                <v-btn v-if="enablePackageRemoval"
                                  @click="removePackage(idx)"
                                  absolute bottom right fab icon small color="white"
                                  style="margin-right: -6px; margin-bottom: 6px;"
                                >
                                  <v-icon color="light-blue" medium>remove_circle_outline</v-icon></v-btn>
                              </v-flex>
                            </v-layout>
                          </v-container>
                        </v-flex>
                        <!-- WMS -->
                        <v-flex xs12 v-if="wmsOrder">
                          <div class="title">{{ $t('Warehouse') }}</div>

                          <v-container fluid grid-list-xs class="px-0 py-4">
                            <!-- FIXME: has items, but doesnt show any rows (2023-07-07) -->
                            <!-- <v-data-table
                              :headers="[
                                { text: $t('Product name'), value: 'product_name',              align: 'left',   sortable: true },
                                { text: $t('Product code'), value: 'product_code',              align: 'left',   sortable: true },
                                { text: $t('Weight')+', '+$t('kg'), value: 'weight', align: 'right',  sortable: true },
                                { text: $t('Qty'),          value: 'quantity',     align: 'right',  sortable: true },
                              ]"
                              :items="wmsOrder.detail"
                              item-key="product_code"
                              class="elevation-1"
                            ></v-data-table> -->

                            <v-layout v-for="(item, idx) in wmsOrder.detail" :key="idx"
                              row wrap
                              style="position: relative;"
                            >
                              <v-flex xs1>
                                <v-text-field :value="idx+1" box hide-details disabled></v-text-field>
                              </v-flex>
                              <v-flex xs2>
                                <v-text-field :value="item.quantity" :label="$t('Quantity')" box hide-details disabled></v-text-field>
                              </v-flex>
                              <v-flex xs2>
                                <v-text-field :value="item.product_code" :label="$t('Code')" box hide-details disabled></v-text-field>
                              </v-flex>
                              <v-flex xs4 md5>
                                <v-text-field :value="item.product_name" :label="$t('Name')" box hide-details disabled></v-text-field>
                              </v-flex>
                              <v-flex xs3 md2>
                                <v-text-field :value="item.weight" :label="$t('Weight')" box hide-details disabled></v-text-field>
                              </v-flex>
                            </v-layout>
                          </v-container>
                        </v-flex>
                      </v-layout>
                    </v-container>
                    </v-form>
                  </v-stepper-content>

                  <v-stepper-content step="4" :class="$vuetify.breakpoint.xsOnly && 'pa-0'">
                    <v-form ref="form4">
                    <v-container fluid grid-list-xs>
                      <v-layout row wrap>
                        <v-flex xs12 sm6 md4>
                          <v-service-selector v-model="shipment.service.type" :type="serviceType" :is-city="isCityServiceType" :allowed="userOpts.shipmentServicesAllowed" multi="false" :max-days="isMaxDaysType"></v-service-selector>
                        </v-flex>
                        <v-flex xs12 sm6 md2>
                          <v-tooltip bottom v-if="userOpts.saturdayAllowed">
                            <v-checkbox slot="activator" v-model="shipment.saturday" :label="$t('Saturday delivery')"></v-checkbox>
                            <span>{{ $t('Saturday delivery explained') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md4>
                          <v-select-enum v-model="shipment.payment.method" :items="paymentMethodList" :label="$t('Payment Method')" type="PaymentMethod" :disabled="!userOpts.paymentMethodEditable"></v-select-enum>
                        </v-flex>
                        <v-flex xs12 sm6 md2>
                          <v-checkbox v-model="shipment.payment.cash" :label="$t('Cash payment')" :disabled="!userOpts.paymentMethodEditable"></v-checkbox>
                        </v-flex>
                      </v-layout>

                      <!-- D A T E S -->

                      <v-layout row wrap v-if="calendar.sourceDate">
                        <v-flex xs12 sm6 md3>
                          <v-menu
                            v-model="calendar.source"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            full-width
                            min-width="290px"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field v-model="calendar.sourceDate"
                                :label="$t('Date from')"
                                prepend-icon="event"
                                readonly
                                v-on="on"
                                hint=" " persistent-hint
                                ref="calendarSourceDate"
                              ></v-text-field>
                            </template>
                            <v-date-picker v-model="calendar.sourceDate" scrollable
                              :allowed-dates="allowedSourceDates"
                              @input="calendarSourceChanged"
                              first-day-of-week="1"
                            ></v-date-picker>
                          </v-menu>
                        </v-flex>
                        <v-flex xs12 sm6 md3>
                          <v-menu
                            v-if="!isCityServiceUserTime"
                            v-model="calendar.sourceTime"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            full-width
                            min-width="320px"
                            max-width="320px"
                            content-class="white"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                :value="printDateTime(shipment.source.after, 2) + ' — ' + printDateTime(shipment.source.until, 2)"
                                prepend-icon="access_time"
                                readonly
                                v-on="on"
                              ></v-text-field>
                            </template>
                            <v-card>
                              <v-toolbar color="primary" dark dense>
                                <v-toolbar-title style="width: 100%">
                                  <v-layout row wrap>
                                    <v-flex class="xs5 caption text-xs-right grey--text text--lighten-1">{{ $t('From') }}</v-flex>
                                    <v-flex class="xs1"></v-flex>
                                    <v-flex class="xs6 caption grey--text text--lighten-1">{{ $t('To') }}</v-flex>
                                    <v-flex class="xs5 text-xs-right" style="line-height: 1.4rem">{{ time2str(calendar.sourceTimeAfter) }}</v-flex>
                                    <v-flex class="xs1 text-xs-center" style="line-height: 1.4rem">-</v-flex>
                                    <v-flex class="xs6" style="line-height: 1.4rem">{{ time2str(calendar.sourceTimeUntil) }}</v-flex>
                                  </v-layout>
                                </v-toolbar-title>
                              </v-toolbar>
                              <v-layout row wrap justify-end align-center
                                style="max-height: 600px"
                                class="scroll-y"
                              >
                                <v-flex xs6 pr-1>
                                  <v-list subheader dense>
                                    <template v-for="(item, index) in calendarTimeItems(this.calendar.sourceWorktime)">
                                      <v-list-tile
                                        :key="index"
                                        :value="calendar.sourceTimeAfter == item.value"
                                        @click="calendar.sourceTimeAfter  = item.value"
                                        :disabled="item.value < calendarSourceTimeLimits.minAfter || item.value > calendarSourceTimeLimits.max"
                                        active-class="primary lighten-2 white--text"
                                        :class="calendarTimesClass(item.value, true)"
                                      >
                                        <v-list-tile-content>
                                          <v-list-tile-title v-text="item.text" class="text-xs-right"></v-list-tile-title>
                                        </v-list-tile-content>
                                      </v-list-tile>
                                    </template>
                                  </v-list>
                                </v-flex>
                                <v-flex xs6 pl-1>
                                  <v-list subheader dense>
                                    <template v-for="(item, index) in calendarTimeItems(this.calendar.sourceWorktime)">
                                      <v-list-tile
                                        :key="index"
                                        :value="calendar.sourceTimeUntil == item.value"
                                        @click="calendar.sourceTimeUntil  = item.value"
                                        :disabled="item.value < calendarSourceTimeLimits.min || item.value > calendar.sourceWorktime.until"
                                        active-class="primary lighten-2 white--text"
                                        :class="calendarTimesClass(item.value, true)"
                                      >
                                        <v-list-tile-content>
                                          <v-list-tile-title v-text="item.text"></v-list-tile-title>
                                        </v-list-tile-content>
                                      </v-list-tile>
                                    </template>
                                  </v-list>
                                </v-flex>
                              </v-layout>
                              <v-divider></v-divider>
                              <v-card-text v-if="calendarSourceTimeErrors.length" class="pa-2">
                                <ul>
                                  <li class="error--text py-1" v-for="(rule, idx) in calendarSourceTimeErrors" :key="idx">{{ rule }}</li>
                                </ul>
                              </v-card-text>
                              <v-card-actions>
                                <v-layout row align-center>
                                  <v-flex>
                                    <v-btn icon flat color="error" @click="calendar.sourceTime = false"><v-icon>close</v-icon></v-btn>
                                  </v-flex>
                                  <v-flex shrink text-xs-right>
                                    <v-btn flat color="success" @click="calendarSourceTimesChanged"
                                      :disabled="!!calendarSourceTimeErrors.length"
                                    ><v-icon left>done</v-icon>{{ $t('Apply') }}</v-btn>
                                    <!--<v-btn flat color="primary" @click="calendarSourceRecalc"
                                      class="ml-0"
                                      :disabled="!!calendarSourceTimeErrors[]"
                                    ><v-icon left>update</v-icon>{{ $t('Apply') }}</v-btn>--><!-- published_with_changes | autorenew | restart_alt -->
                                  </v-flex>
                                </v-layout>
                              </v-card-actions>
                            </v-card>
                          </v-menu>

                          <v-menu
                            v-else
                            v-model="calendar.sourceTime"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            full-width
                            min-width="320px"
                            max-width="320px"
                            content-class="white"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                :value="printDateTime(shipment.source.after, 2) + ' - ' + printDateTime(shipment.source.until, 2)"
                                prepend-icon="access_time"
                                readonly
                                v-on="on"
                              ></v-text-field>
                            </template>
                            <v-card>
                              <v-toolbar color="primary" dark dense>
                                <v-toolbar-title style="width: 100%">
                                  <v-layout row wrap>
                                    <v-flex class="xs12 caption grey--text text--lighten-1">{{ $t('From') }}</v-flex>
                                    <v-flex class="xs12" style="line-height: 1.4rem">{{ time2str(calendar.sourceTimeAfter) }}</v-flex>
                                  </v-layout>
                                </v-toolbar-title>
                              </v-toolbar>
                              <v-layout row wrap justify-end align-center
                                style="max-height: 600px"
                                class="scroll-y"
                              >
                                <v-flex>
                                  <v-list subheader dense>
                                    <template v-for="(item, index) in calendarTimeItems(this.calendar.sourceWorktime)">
                                      <v-list-tile
                                        :key="index"
                                        :value="calendar.sourceTimeAfter == item.value"
                                        @click="calendar.sourceTimeAfter  = item.value; calendarSourceTimesChanged()"
                                        active-class="primary white--text"
                                        :class="calendarTimesClass(item.value)"
                                      >
                                        <v-list-tile-content>
                                          <v-list-tile-title v-text="item.text"></v-list-tile-title>
                                        </v-list-tile-content>
                                      </v-list-tile>
                                    </template>
                                  </v-list>
                                </v-flex>
                              </v-layout>
                            </v-card>
                          </v-menu>
                        </v-flex>

                        <!-- TARGET -->

                        <v-flex xs12 sm6 md3>
                          <v-menu
                            v-model="calendar.target"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            full-width
                            min-width="290px"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                               :value="printDateTime(shipment.target.after, 1) + ' (' + dayOfWeek(shipment.target.after) + ')' + (
                                    !isSameDay(shipment.target.after, shipment.target.until) ? ' ' + printDateTime(shipment.target.after, 2) : ''
                               )"
                               :label="$t('Expected delivery') + (
                               !isSameDay(shipment.target.after, shipment.target.until) ? ', ' + $t('From') : ''
                               )"
                               prepend-icon="date_range"
                               readonly :disabled="isCalendarTargetDateDisabled"
                               v-on="on"
                               hint=" " persistent-hint
                               ref="calendarTargetDate"
                              >
                              </v-text-field>
                            </template>
                            <v-date-picker v-model="calendar.targetDate" scrollable
                              :allowed-dates="allowedTargetDates"
                              @input="calendarTargetChanged"
                              first-day-of-week="1"
                            ></v-date-picker>
                          </v-menu>
                        </v-flex>

                        <v-flex xs12 sm6 md3>
                          <v-menu
                            v-model="calendar.targetTime"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            full-width
                            min-width="320px"
                            max-width="320px"
                            content-class="white"
                            :disabled="isCalendarTargetTimesDisabled"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                :value="(
                                  isSameDay(shipment.target.after, shipment.target.until) ? printDateTime(shipment.target.after, 2) + ' — ' : printDateTime(shipment.target.until, 1) + ' (' + dayOfWeek(shipment.target.until) + ') '
                                ) + printDateTime(shipment.target.until, 2)"
                                :label="!isSameDay(shipment.target.after, shipment.target.until) ? $t('To') : null"
                                :prepend-icon="isSameDay(shipment.target.after, shipment.target.until) ? 'access_time' : 'remove'"
                                readonly :class="isCalendarTargetTimesDisabled && 'disabled'"
                                persistent-hint :hint="isCalendarTargetTimesDisabled ? ($t('Selected service is') + ' ' + $t('ServiceType.' + shipment.service.type)) : ''"
                                v-on="on"
                              ></v-text-field>
                            </template>
                            <v-card>
                              <v-toolbar color="primary" dark dense>
                                <v-toolbar-title style="width: 100%">
                                  <v-layout row wrap>
                                    <v-flex class="xs12 caption grey--text text--lighten-1">{{ $t('To') }}</v-flex>
                                    <v-flex class="xs12" style="line-height: 1.4rem">{{ time2str(calendar.targetTimeUntil) }}</v-flex>
                                  </v-layout>
                                </v-toolbar-title>
                              </v-toolbar>
                              <v-layout row wrap justify-end align-center
                                style="max-height: 600px"
                                class="scroll-y"
                              >
                                <v-flex>
                                  <v-list subheader dense>
                                    <template v-for="(item, index) in calendarTargetTimeItems()">
                                      <v-list-tile
                                        :key="index"
                                        :value="calendar.targetTimeUntil == item.value"
                                        @click="calendar.targetTimeUntil  = item.value"
                                        :disabled="item.value < calendarTargetTimeLimits.min || item.value > calendar.targetWorktime.until"
                                        active-class="primary white--text"
                                        :class="calendarTimesClass(item.value)"
                                      >
                                        <v-list-tile-content>
                                          <v-list-tile-title v-text="item.text"></v-list-tile-title>
                                        </v-list-tile-content>
                                      </v-list-tile>
                                    </template>
                                  </v-list>
                                </v-flex>
                              </v-layout>
                              <v-divider></v-divider>
                              <v-card-text v-if="calendarTargetTimeErrors.length" class="pa-2">
                                <ul>
                                  <li class="error--text py-1" v-for="(rule, idx) in calendarTargetTimeErrors" :key="idx">{{ rule }}</li>
                                </ul>
                              </v-card-text>
                              <v-card-actions>
                                <v-layout row align-center>
                                  <v-flex>
                                    <v-btn icon flat color="error" @click="calendar.targetTime = false"><v-icon>close</v-icon></v-btn>
                                  </v-flex>

                                  <v-flex shrink text-xs-right>
                                    <v-btn flat color="success" @click="calendarTargetTimesChanged"
                                      :disabled="!!calendarTargetTimeErrors.length"
                                    ><v-icon left>done</v-icon>{{ $t('Apply') }}</v-btn>
                                  </v-flex>
                                </v-layout>
                              </v-card-actions>
                            </v-card>
                          </v-menu>
                        </v-flex>
                      </v-layout>

                      <v-layout row wrap align-center>
                        <!-- COD -->
                        <v-flex xs12 sm6 md3 lg3>
                          <v-tooltip bottom>
                            <v-badge overlap left bottom slot="activator" class="mb-4">
                              <template v-slot:badge v-if="prices.length > 0 && prices[0].components.CASH_ON_DELIVERY">$</template>
                              <v-checkbox v-model="shipment.cash_on_delivery.cash_on_delivery" :label="$t('Cash On Delivery')" hide-details></v-checkbox>
                            </v-badge>
                            <span>{{ $t('Cash On Delivery') }}
                              <span v-if="prices.length > 0 && prices[0].components.CASH_ON_DELIVERY"> (+{{ showAmount(prices[0].components.CASH_ON_DELIVERY) }})</span>
                            </span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md3 lg3>
                          <v-tooltip v-if="shipment.cash_on_delivery.cash_on_delivery" bottom>
                            <v-checkbox slot="activator" v-model="shipment.cash_on_delivery.postal" :label="$t('Postal')" :disabled="!userOpts.pmtOnCodEditable"></v-checkbox>
                            <span>{{ $t('Postal money transfer') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md3 lg4>
                          <v-text-field v-if="shipment.cash_on_delivery.cash_on_delivery" v-model="shipment.cash_on_delivery.amount.amount" type="number" :label="$t('Cash On Delivery Amount')"
                            :prefix="currencySymbol(shipment.cash_on_delivery.amount, true)"
                            :suffix="currencySymbol(shipment.cash_on_delivery.amount, false)"
                            :error-messages="errors(['cash_on_delivery.amount.amount', 'cashFromRecipient'], 4)"
                            @keyup="clearErrors(['cash_on_delivery.amount.amount', 'cashFromRecipient'])"
                            :min="limits.cashOnDeliveryAmount.min"
                            :max="limits.cashOnDeliveryAmount.max"
                            :rules="[rules.numberFormat, rules.cashOnDeliveryAmount]" />
                        </v-flex>
                        <v-flex xs12 sm6 md3 lg2>
                          <v-tooltip v-if="shipment.cash_on_delivery.cash_on_delivery" bottom>
                            <v-select-enum slot="activator" v-model="shipment.cash_on_delivery.payment_type" :items="codPaymentList" :label="$t('COD Payment Type')" type="CodPaymentType"></v-select-enum>
                            <span>{{ $t('COD Payment Type tooltip') }}</span>
                          </v-tooltip>
                        </v-flex>

                        <!-- Insurance -->
                        <v-flex xs12 sm6 md3>
                          <v-tooltip bottom>
                            <v-badge overlap left bottom slot="activator" class="mb-4">
                              <template v-slot:badge v-if="prices.length > 0 && prices[0].components.INSURANCE">$</template>
                              <v-checkbox v-model="shipment.insurance.active" :label="$t('Insurance')" hide-details></v-checkbox>
                            </v-badge>
                            <span>{{ $t('Insurance') }}
                              <span v-if="prices.length > 0 && prices[0].components.INSURANCE"> (+{{ showAmount(prices[0].components.INSURANCE) }})</span>
                            </span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md3>
                          <v-checkbox v-if="shipment.insurance.active" v-model="shipment.insurance.fragile" :label="$t('Fragile')"></v-checkbox>
                        </v-flex>
                        <v-flex xs12 sm6 md3>
                          <v-checkbox v-if="shipment.insurance.active" v-model="shipment.insurance.used" :label="$t('Used')"></v-checkbox>
                        </v-flex>
                        <v-flex xs12 sm6 md3>
                          <v-text-field v-if="shipment.insurance.active" v-model="shipment.insurance.liability.amount" type="number" :label="$t('Liability Amount')"
                            :prefix="currencySymbol(shipment.insurance.liability, true)"
                            :suffix="currencySymbol(shipment.insurance.liability, false)"
                            :error-messages="errors(['insurance.liability.amount', 'insurance.liability'], 4)"
                            @keyup="clearErrors('insurance.liability')"
                            :min="limits.insuranceAmount.min"
                            :max="limits.insuranceAmount.max"
                            :rules="[rules.numberFormat, rules.insuranceAmount]" />
                        </v-flex>

                        <!-- -->
                        <v-flex xs12 sm6 md3>
                          <v-tooltip bottom>
                            <v-select-enum slot="activator" v-model="shipment.packaging_handling" :items="packagingHandlingListAvailable" :label="$t('Packaging Handling')" type="PackagingHandling"></v-select-enum>
                            <span>{{ $t('Packaging Handling tooltip') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs12 sm6 md3>
                          <v-checkbox v-model="shipment.packaging_stretching" :label="$t('Stretching')"></v-checkbox>
                        </v-flex>
                        <!-- Test, Review on Delivery -->
                        <v-flex xs6 md3>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.test_on_delivery" :label="$t('Test')"/>
                            <span>{{ $t('Test On Delivery') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <v-flex xs6 md3>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment.review_on_delivery" :label="$t('Review')"/>
                            <span>{{ $t('Review On Delivery') }}</span>
                          </v-tooltip>
                        </v-flex>

                        <!-- Connected shipments -->
                        <v-flex v-for="cs in connectedShipments" :key="cs" xs12 sm6 md3>
                          <v-tooltip bottom>
                            <v-checkbox slot="activator" v-model="shipment[cs]" @change="checkConnectedShipments(cs)" :disabled="connectedShipmentsDisabled[cs]" hide-details class="ma-0 pa-0">
                              <template v-slot:label>
                                {{ $t('ConnectedShipment.' + cs) }}
                                <template v-if="cs === 'return_doc'">
                                  <attachments key="cs" :shipment="shipment" :isShipmentReadonly="isShipmentReadonly" :flag="cs" />
                                </template>
                              </template>
                            </v-checkbox>
                            <span>{{ $t('ConnectedShipment.' + cs + '.tip') }}</span>
                          </v-tooltip>
                        </v-flex>
                        <!-- Other documents -->
                        <v-flex xs12 sm6 md3>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <div class="d-flex align-center" v-bind="attrs" v-on="on">
                                <span class="v-label theme--light">{{ $t('OtherDocuments') }}</span>
                                <attachments key="return_oth" :shipment="shipment" :isShipmentReadonly="isShipmentReadonly" flag="return_oth" />
                               </div>
                            </template>
                            <span>{{ $t('OtherDocuments.tip') }}</span>
                          </v-tooltip>
                        </v-flex>
                      </v-layout>
                    </v-container>
                    </v-form>
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>

              </div>
              <div v-show="screen === 'SINGLE_PAGE'">

              <v-expansion-panel v-model="screenModesData.expanel" expand>
                <v-expansion-panel-content v-for="(item, idx) in [
                    $t('Source Partner'), $t('Target Partner'), $t('Packages'), $t('Services')
                  ]" :key="idx"
                >
                  <template v-slot:header>
                    <div class="subheading text-uppercase primary--text  text--lighten-2">{{ item }}</div>
                  </template>
                  <v-card>
                    <v-card-text></v-card-text>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>

              </div>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <v-card-actions><v-layout wrap :class="[
        $vuetify.breakpoint.xsOnly && 'actions-bar-mobile',
        priceBox.float && 'is-floating'
      ]">
        <v-spacer></v-spacer>
        <v-btn v-if="!priceBox.float" :loading="!validPrice" color="blue darken-1" flat @click.stop="prices.length > 0 && (pricesMenu = !pricesMenu)" id="pricesActivator">
          <v-tooltip bottom v-if="prices.length > 0">
            <span slot="activator" class="d-flex">
              <v-icon>info</v-icon>&nbsp;
            </span>
            {{ $t('Price details for all service types') }}
          </v-tooltip>

          {{ $t('Price') }}&nbsp;
          {{ courierShipmentText }}:&nbsp;
          <v-tooltip bottom>
            <span slot="activator" v-html="shipmentPrice"></span>
            {{ $t('without VAT') }}
          </v-tooltip>

          <v-icon right small @click="nextPriceBox('float')">north_east</v-icon>
        </v-btn>
        <v-tooltip bottom>

        </v-tooltip>
        <v-btn v-if="!calculator &&  shipment.accepted  && !shipment.cancelled && shipment.owned"      color="blue darken-1" flat @click.native="printLabel"><v-icon>label</v-icon>&nbsp;{{ $t('Label') }}</v-btn>
        <v-btn v-if="!calculator &&  shipment.accepted  && !shipment.cancelled && shipment.owned"      color="blue darken-1" flat @click.native="printMenu" ><v-icon>print</v-icon>&nbsp;{{ $t('Manifest') }}</v-btn>
        <v-btn v-if="!calculator && !shipment.collected && !shipment.cancelled && shipment.owned"      color="blue darken-1" flat @click.native="save"      ><v-icon>save</v-icon>&nbsp;{{ $t('Save') }}</v-btn>
        <v-btn v-if="!calculator && !shipment.accepted  && shipment.owned" :disabled="isOrderDisabled" color="blue darken-1" flat @click.native="order"     ><v-icon>shop</v-icon>&nbsp;{{ $t('Order') }}</v-btn>
        <v-btn v-if="!calculator &&  shipment.cancellable"                                             color="grey"          flat @click.native="cancel"    ><v-icon>cancel</v-icon>&nbsp;{{ $t('Cancel') }}</v-btn><!--Anulirane-->
      </v-layout></v-card-actions>

      <v-dialog v-model="dangerousGoodsShow" :overlay="false" max-width="800px" transition="dialog-transition">
        <dangerous-goods v-if="activeAdrParcel" :key="shipment.contents.indexOf(activeAdrParcel)"
          :value="activeAdrParcel.dangerous_goods" :shown="dangerousGoodsShow"
          :allow-adr="adrAllowed(activeAdrParcel)" @input="setDangerousGoods"
          @blocked="msg($t('ADR.palletRequired'), 'error')" @done="dangerousGoodsShow = false" />
      </v-dialog>

      <v-menu v-if="prices.length > 0"
        v-model="pricesMenu"
        :activator="priceBox.float ? '#pricesFloatActivator' : '#pricesActivator'"
        :left="priceBox.x === 'right'"
        :offset-y="priceBox.y === 'top' || !priceBox.float"
        offset-overflow
        :close-on-content-click="false"
        style="z-index: 1001"
      >
        <v-data-iterator
          :items="$vuetify.breakpoint.xsOnly ? prices.filter(price => price.service_type === (pricesItem || shipment.service.type)) : prices"
          content-tag="v-layout"
          row :wrap="$vuetify.breakpoint.xsOnly"
          hide-actions
          class="white"
          style="overflow: auto"
        >
          <template v-slot:item="props">
            <v-flex @click.stop>
              <v-card class="pa-0 elevation-0">
                <v-list dense subheader class="price-row caption" :class="{'light-blue lighten-5': props.item.service_type === shipment.service.type}">
                  <v-list-tile class="price-row-group">
                    <v-list-tile-content v-if="props.index == 0 && $vuetify.breakpoint.xsOnly">
                      <v-select :value="pricesItem || shipment.service.type" @change="pricesItem = $event" :items="pricesItems" dense></v-select>
                    </v-list-tile-content>
                    <v-list-tile-content v-else :class="props.index == 0 ? 'align-end' : 'align-center'" class="caption font-weight-bold mr-2">{{ $t("ServiceType." + props.item.service_type) }}</v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile>
                    <v-list-tile-content v-if="props.index == 0" class="font-weight-bold mr-4">{{ $t("without VAT") }} </v-list-tile-content>
                    <v-list-tile-content class="align-end text-no-wrap" v-html="showAmount(props.item.price, true)"></v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile :class="Object.keys(props.item.components).length > 0 ? 'price-row-group' : ''">
                    <v-list-tile-content v-if="props.index == 0" class="font-weight-bold mr-4">{{ $t("with VAT") }} </v-list-tile-content>
                    <v-list-tile-content class="align-end text-no-wrap" v-html="showAmount(props.item.price_tax, true)"></v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile v-if="props.item.TRANSPORT">
                    <v-list-tile-content v-if="props.index == 0" class="mr-4">{{ $t("Price.TRANSPORT") }} </v-list-tile-content>
                    <v-list-tile-content class="align-end text-no-wrap" v-html="showAmount(props.item.TRANSPORT, true)"></v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile v-if="props.item.FUEL_TAX">
                    <v-list-tile-content v-if="props.index == 0" class="mr-4">{{ $t("Price.FUEL_TAX") }} </v-list-tile-content>
                    <v-list-tile-content class="align-end text-no-wrap" v-html="showAmount(props.item.FUEL_TAX, true)"></v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile v-for="(comp, key, idx) in props.item.components" :key="idx">
                    <v-list-tile-content v-if="props.index == 0" class="mr-4">{{ $t("Price." + key ) }} </v-list-tile-content>
                    <v-list-tile-content class="align-end text-no-wrap" v-html="showAmount(comp, true)"></v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-card>
            </v-flex>
          </template>
        </v-data-iterator>
      </v-menu>

      <v-snackbar multi-line :timeout="0"
         color="info"
        :value="priceBox.float"
        :top="priceBox.y === 'top'"
        :bottom="priceBox.y === 'bottom'"
        :left="priceBox.x === 'left'"
        :right="priceBox.x === 'right'"
      >
        <v-container class="ma-0 pa-0 pricesActivator" :style="$vuetify.breakpoint.xsOnly ? '' : 'min-width: 400px; min-height: 50px; position: relative;'">
          <v-layout v-resize="resizePriceBox" row>
            <v-flex :class="$vuetify.breakpoint.xsOnly ? 'xs2' : 'xs3'">{{ $t('Price') }}</v-flex>
            <v-flex grow>
              <template v-if="validPrice">
                <v-tooltip bottom v-if="prices.length > 0">
                  <span slot="activator" class="d-flex" @click="pricesMenu = !pricesMenu" id="pricesFloatActivator">
                    <span v-html="shipmentPrice"></span> ({{ $t('without VAT') }})
                    <v-icon dark class="ml-2 justify-start" style="margin-top: -2px">info</v-icon>
                  </span>
                  {{ $t('Price details for all service types') }}
                </v-tooltip>
                <template v-else><span v-html="shipmentPrice"></span> ({{ $t('without VAT') }})</template>
              </template>
              <v-progress-circular v-else indeterminate></v-progress-circular>
            </v-flex>

            <v-flex shrink class="text-xs-right">
              <v-btn color="blue darken-3" @click="nextPriceBox('pos')" icon class="mx-2"><v-icon small>{{ priceBox.icon }}</v-icon></v-btn>
              <v-btn color="blue darken-3" @click="nextPriceBox('float')" icon class="ma-0"><v-icon small>close</v-icon></v-btn>
            </v-flex>
          </v-layout>

          <v-layout v-resize="resizePriceBox" row>
            <v-flex :class="$vuetify.breakpoint.xsOnly ? 'xs2'  : 'xs3'">{{ courierShipmentText }}</v-flex>
            <v-flex :class="$vuetify.breakpoint.xsOnly ? 'xs10' : 'xs9'"><span v-if="validPrice" v-html="shipmentPriceVat"></span> ({{ $t('with VAT') }})</v-flex>
          </v-layout>
        </v-container>
      </v-snackbar>

    </v-card>
  </v-container>
  </v-content>
</template>

<script>
import { phoneRules, shipmentPhoneCountry } from "../components/phone";
import PrLoading from "@/components/Loading.vue";
import VSuggestSettlement from "@/components/VSuggestSettlement.vue";
import VSuggestOffice from "@/components/VSuggestOffice.vue";
import VSuggestContact from "@/components/VSuggestContact.vue";
import VServiceSelector from "@/components/VServiceSelector.vue";
import VPackageSelector from "@/components/VPackageSelector.vue";
import VSelectEnum from "@/components/VSelectEnum.vue";
import DangerousGoods from "@/components/DangerousGoods.vue";
import { ADR_PALLET_MESSAGE, hasAdr, allowsAdr } from "@/components/adrPackaging";
import { shipments, suggest } from "../resources";
import { getDefaultShipment, getDefaultServiceTypes, downloadFile, toNumber, postProcessMessageDOM, isShipmentReadonly, showAmount } from "../components/util.js";
import { mapGetters, mapActions } from "vuex";
import { debounce } from "lodash";
import TrackingProgress from "@/components/TrackingProgress.vue";
import Contact from "@/views/Contact.vue";
import Attachments from "@/components/Attachments.vue";

const LIMITS = {
  cashOnDeliveryAmount: { min: 0, max: 10000 },
  insuranceAmount:      { min: 0, max: 99999 }
}

export default {
  components: {
    PrLoading,
    VSuggestSettlement,
    VSuggestOffice,
    VSuggestContact,
    VSelectEnum,
    VServiceSelector,
    VPackageSelector,
    DangerousGoods,
    TrackingProgress,
    Contact,
    Attachments
  },

  props: ['id', 'poke'],

  data() {
    return {
      loading: false,
      showMsg: false,
      msgText: '',
      msgType: '',
      msgTimeout: 3000,
      showConfirm: false,

      calculator: false,
      screen: null,
      validPrice: true,
      validDelivery: true,
      loadDisabled: false,

      priceBox: {
        float: true,
        x: 'left',
        y: 'bottom',
        icon: 'expand_less'
      },

      step: 1,
      errorDetails: false,
      errorMessagesVersion: 1,
      errorMessages: new Map(),
      errorConsumed: new Map(),

      vRequired: [(v) => !!v || this.$t('This field is required!')],
      vRequiredString: [(v) => !(!v || /^\s*$/.test(v) ) || this.$t('This field is required!')],
      vRequireSourceCode: [(v) => (!this.shipment.source.direct || (!!v && !!v.code)) || this.$t('This field is required!')],
      vRequireTargetCode: [(v) => (!this.shipment.target.direct || (!!v && !!v.code)) || this.$t('This field is required!')],
      vRequiredDescription: [val => !!val && String(val).replaceAll(/\s/gm, '').length >= 3 || this.$t('This field cannot be empty!')],
      vRequiredPackaging: [val => (!!val && !!val.code) || this.$t('This field cannot be empty!')],
      vRequiredNumber: [(val) => !!val && val > 0 || this.$t('Required value!')],
      vRequiredAmazon: [(v) => (!!v || !!this.shipment.target.address && !!this.shipment.target.address.country && this.shipment.target.address.country.code === 'BG') || this.$t('Amazon Reference.error')],
      vRequiredContact: [(val) => (!!val && !!val.partner && !!val.partner.name) || this.$t('This field is required!'), (val) => !/^[.,;:!?\-_\s|*+]+$/.test(val.partner.name) || this.$t('Valid partner name')],
      vRequiredOffice: [(val) => (!!val && !!val.office && !!val.office.name) || this.$t('This field is required!')],

      screenModesData: {
        disabled: true,
        expanel: [true, true, true, true],
        contents: [],
        stepperContainers: null,
        expanelContainers: null,
      },
      screenModes: [
        'TABBED',
        'SINGLE_PAGE'
      ],
      paymentMethodList: [
        'PAYED_ON_SENDING',
        'PAYED_ON_DELIVERY',
        'PAYED_BY_CONTRACT'
      ],
      accessList: [
        null,
        'VAN',
        'TRUCK'
      ],
      codPaymentList: [
        null,
        'CASH',
        'BANK_TRANSFER'
      ],
      packagingHandlingList: [
        null,
        'PURCHASE',
        'RENTING'
      ],
      connectedShipments: [
        "return_doc",
        "return_pod",
        "return_pkg"
      ],

      shipment: getDefaultShipment(),

      showAmazonReference: false,
      isInitiallyLoading: false,
      shipmentStored: {},

      calendar: {
        source: false,// show source date widget
        sourceDate: null,
        sourceTime: false,// show source time widget
        sourceTimeAfter: null,
        sourceTimeUntil: null,
        sourceServicing: [],
        sourceWorktime: {
          after:  900,
          until: 1800
        },
        sourceTimeMinGap: 200,
        sourceTimeUntilIsBefore14: false,
        //
        targetDate: null,
        targetTime: false,// show target date widget
        targetTimeAfter: null,
        targetTimeUntil: null,
        targetServicing: [],
        targetServicingType: null,
        targetWorktime: {
          after:  900,
          until: 1800
        },
        //
        timeNowInterval: null,
        timeNow: null
      },

      prices: [],
      //pricesComponents: false,
      pricesItem: '',
      pricesMenu: false,

      contact: { show: false, data: null },

      contactSuggestOwn: {
        source: true,
        target: false
      },

      personalNumber: {
        panel: false,
        rules: [(v) => this.isPersonalNumberRules || this.$t("Personal number.invalid")]
      },
      shipmentTypes: [
        "NETWORK",
        "COMPLEX",
        "COMPLEX-NETWORK-FROM", // in userOpts: "COMPLEX-NETWORK"
        "COMPLEX-NETWORK-TO"
      ],
      shipmentType: "NETWORK",

      isCourierShipmentDeliveryDate: null,

      isShipmentSourceOfficeDisabled: false,

      station: {
        source: {
          disabled: false,
          value: { code: null, name: "", distance: null }
        },
        target: {
          disabled: false,
          value: { code: null, name: "", distance: null }
        },
      },

      dangerousGoodsShow: false,
      activeAdrParcel: null,

      printOpts: { show: false, price: true, insurance: true },
      userOpts: {
        sourceDoorEditable: true,
        targetDoorEditable: true,
        packageReturn: null,
        packaging: { "null": {} },// collect used packaging data (standard, small, returnable)
        shipmentService:     null,
        cityShipmentService: null,
        pmtOnCodEditable: false,
        paymentMethodEditable: true,
        shipmentServicesAllowed: {
          COURIER_CITY_150: null,
          COURIER_CITY_EXPRESS: null,
          EXPRESS_14: true,
          EXPRESS_12: true,
          EXPRESS_10: true
        }
      },
      packList: [], // to be loaded
      units: {
        parcelDims: "m"
      },
      prefs: {
        units: {
          parcelDims: [
            { id: "m",  ratio: 0 },
            { id: "cm", ratio: 0.01 }
          ]
        }
      },
      limits: {
        cashOnDeliveryAmount: { min: LIMITS.cashOnDeliveryAmount.min, max: LIMITS.cashOnDeliveryAmount.max },
        insuranceAmount:      { min: LIMITS.insuranceAmount.min,      max: LIMITS.insuranceAmount.max      }
      },
      rules: {
        numberFormat: (val) => !!(val+'') && !isNaN(val+'') || this.$t('Incorrect number format!'),
        numberNonNegative: (val) => !!(val+'') && val>=0 || this.$t('Value required!'),
        numberPositive: (val) => !!(val+'') && val>0 || this.$t('Value required!'),
        cashOnDeliveryAmount: (val) => (val >= LIMITS.cashOnDeliveryAmount.min && val <= LIMITS.cashOnDeliveryAmount.max) || this.$t('Out of limits!', [LIMITS.cashOnDeliveryAmount.min, LIMITS.cashOnDeliveryAmount.max]),
        insuranceAmount:      (val) => (val >= LIMITS.insuranceAmount.min      && val <= LIMITS.insuranceAmount.max     ) || this.$t('Out of limits!', [LIMITS.insuranceAmount.min,      LIMITS.insuranceAmount.max]),
        // phoneFormat: (val) => {
        //   if (!val || !val.length) return this.$t('This field is required!');
        //   return val.match(/^\+?[0-9]+/) && val.length >= 4 || this.$t('Incorrect phone number format!');
        // },
      },
      showAmount,
      costCenters: []
    }
  },

  computed: {
    sourcePhoneRules() {
      return phoneRules(key => this.$t(key), { country: shipmentPhoneCountry(this.shipment.source), required: true });
    },
    targetPhoneRules() {
      return phoneRules(key => this.$t(key), { country: shipmentPhoneCountry(this.shipment.target), required: true });
    },
    ...mapGetters(["userInfo", "requestPrefs", "globalConfig", "countries"]),

    progressState() {
      return this.shipment.progress ? this.shipment.progress / 20 : 0;
    },

    shipmentSourceAddress() {
      let p = this.shipment.source;
      let v = "[" + (p.direct ? "X" : " ") + "] ";
      if (p.direct)
        return v + p.address.country.code + "-" + p.address.post_code + " " + p.address.settlement.code;
      else
        return v + (p.office ? p.office.code : (p.station ? p.station.code : ""));
    },

    shipmentTargetAddress() {
      let p = this.shipment.target;
      let v = "[" + (p.direct ? "X" : " ") + "] ";
      if (p.direct)
        return v + p.address.country.code + "-" + p.address.post_code + " " + p.address.settlement.code;
      else
        return v + (p.office ? p.office.code : (p.station ? p.station.code : ""));
    },

    shipmentParcelsWeight() {
      let s = "";
      for (let i = 0; i < this.shipment.contents.length; i ++ )
      {
        let p = this.shipment.contents[i];
        if (s.length > 0)
          s = s + ", ";
        s = s + p.count + "x" + p.packaging.code;
        s = s + " " + p.weight + "kg";
        s = s + " " + p.volume + "m3";
        s = s + " " + p.parcel_x + "-" + p.parcel_y + "-" + p.parcel_z;
        p.dangerous_goods && p.dangerous_goods.forEach((dg, idx) => {
          s = s + " DG" + (idx+1) + ": " + (dg.name || dg.base.name) + " " + dg.weight + "kg" + " class-" + dg.code_classification + " code-" + dg.code_hazard;
        });
      }
      return s;
    },

    shipmentPrice() {
      let p = this.shipment.payment.price.price;
      return p && p.amount > 0 ? this.showAmount(p, true, true) : "N/A";
    },

    shipmentPriceVat() {
      let p = this.shipment.payment.price.price_with_tax;
      return p && p.amount > 0 ? this.showAmount(p, true, true) : "N/A";
    },

    shipmentHasPriceParameters() {
      let has = true;
      for (let idx = 1; idx < 3; idx ++)
      {
        let p = (idx === 1) ? this.shipment.source : this.shipment.target;
        let o = p.direct
          ? (p.address && p.address.settlement && p.address.settlement.code && !isNaN(p.address.settlement.code))
          : (p.station && p.office && p.office.code && !isNaN(p.office.code));
        has = has && o;
      }

      let ok = false;
      for (let i = 0; i < this.shipment.contents.length; i++)
      {
        let c = this.shipment.contents[i];
        let packagingCode = c.packaging.code;
        if (c.count && !isNaN(c.count) && ((c.weight && !isNaN(c.weight)) || (c.volume && !isNaN(c.volume)))) {
          if (this.validDimension(c.parcel_x, packagingCode) && this.validDimension(c.parcel_y, packagingCode) && this.validDimension(c.parcel_z, packagingCode))
            ok = true;
          else
            ok = false;
        }
      }
      has = has && ok;
      return has;
    },

    shipmentServicesOptions() {
      return this.shipment.service.type + "-" + (this.shipment.saturday ? "S" : "N");
    },

    isCalendarTargetTimesDisabled() {
      return this.shipment.service.type === 'EXPRESS_14'
          || this.shipment.service.type === 'EXPRESS_12'
          || this.shipment.service.type === 'EXPRESS_10'
          || this.calendar.targetServicingType === 'max_days';
    },

    isCalendarTargetDateDisabled() {
      return this.calendar.targetServicingType === 'max_days'
      || this.isCityServiceUserTime;
    },

    isCityServiceUserTime() {
      return this.shipment.service.type === 'COURIER_CITY_150'
          || this.shipment.service.type === 'COURIER_CITY_EXPRESS';
    },

    serviceDurationMins() {
      let durmins = 0;
      if (this.shipment.service.type === 'COURIER_CITY_150')
        durmins = 120 + 90;
      if (this.shipment.service.type === 'COURIER_CITY_EXPRESS')
        durmins = 240 + 120;
      return durmins;
    },

    calendarSourceTimeLimits() {
      let after = this.calendar.sourceWorktime.after;
      let until = this.calendar.sourceWorktime.until;

      let afterCalc = Number( this.calendar.sourceTimeAfter ) || after;
      let untilCalc = Number( this.calendar.sourceTimeUntil ) || until;

      let minAfter = after;
      if (this.isToday(this.calendar.sourceDate)) {
        minAfter = Math.max(minAfter, this.calendar.timeNow);
      }

      after = Math.max(after, afterCalc);
      until = Math.min(until, untilCalc);

      return {
        minAfter: minAfter,
        min:         after,
        max:         until
      };
    },

    calendarTargetTimeLimits() {
      let afterNum = this.calendar.targetWorktime.after;
      let untilNum = this.calendar.targetWorktime.until;

      let afterCalc = Number( this.calendar.targetTimeAfter ) || afterNum;
      let untilCalc = Number( this.calendar.targetTimeUntil ) || untilNum;

      afterNum = Math.max(afterNum, afterCalc);
      untilNum = Math.min(untilNum, untilCalc);

      return {
        min: afterNum,
        max: untilNum
      };
    },

    calendarSourceTimeErrors() {
      let errs = [];
      if (!this.calendar.sourceTimeAfter || !this.calendar.sourceTimeUntil) {
        return errs;
      }
      const after = Number( this.calendar.sourceTimeAfter );
      const until = Number( this.calendar.sourceTimeUntil );

      // Rule: after > until
      if (after > until) {
        errs.push( this.$t('Calendar.rule.UntilBeforeAfter') );
      }

      const gap = this.calendar.sourceTimeMinGap;

      // Rule: gap
      if (this.shipment.source.direct    && (this.timeTo100(until) - this.timeTo100(after)  <  this.timeTo100(gap))) {
        const hours = Math.floor( gap / 100 );
        const mins = gap % 100;
        errs.push( this.$t('Calendar.rule.MinimumGap') + ' ' + (hours ? hours + this.$t('h') : '') + (mins ? ' ' + mins + this.$t('m') : '') );
      }

      // Rule: until < 14:00
      if (this.shipment.source.direct    && !this.calendar.sourceTimeUntilIsBefore14    && until < 1400) {
        errs.push( this.$t('Calendar.rule.Until14') );
      }

      // Rule: Day expired - replace other rules
      const calLimitAfterNum = this.calendarSourceTimeLimits.minAfter;
      const calWorktimeUntilNum = this.calendar.sourceWorktime.until;
      if (calLimitAfterNum + gap >= calWorktimeUntilNum) {
        errs = [];
        errs.push( this.$t('Calendar.rule.DayExpired') );
      }
      return errs;
    },

    calendarTargetTimeErrors() {
      let errs = [];
      if (!this.calendar.targetTimeAfter || !this.calendar.targetTimeUntil) {
        return errs;
      }
      const after = Number( this.calendar.targetTimeAfter );
      const until = Number( this.calendar.targetTimeUntil );

      // Rule: after > until
      if (after > until) {
        errs.push( this.$t('Calendar.rule.UntilBeforeAfter') );
      }

      // Rule: gap less than 30min
      if (this.shipment.target.direct    && this.timeTo100(until) - this.timeTo100(after)  <  0.5) {
        errs.push( this.$t('Calendar.rule.MinimumGap30min') );
      }

      // Rule: Speed 10-12-14
      if (until <= 1400 && until > 1200 && this.userOpts.shipmentServicesAllowed.EXPRESS_14) {
        errs.push( this.$t('Calendar.rule.Speed14') );
      }
      else
      if (until <= 1200 && until > 1000 && this.userOpts.shipmentServicesAllowed.EXPRESS_12) {
        errs.push( this.$t('Calendar.rule.Speed12') );
      }
      else
      if (until <= 1000 && this.userOpts.shipmentServicesAllowed.EXPRESS_10) {
        errs.push( this.$t('Calendar.rule.Speed10') );
      }
      return errs;
    },

    shipmentAdditionalOptions() {
      let v = "";
      if (this.shipment.return_pod)
        v += "POD ";
      if (this.shipment.return_doc)
        v += "DOC ";
      if (this.shipment.return_pkg)
        v += "PKG ";

      let//
      cod = this.shipment.cash_on_delivery;
      if (cod && cod.cash_on_delivery) {
        let qty = cod.amount ? cod.amount.amount : 0;
        let cur = cod.amount && cod.amount.currency ? cod.amount.currency.code : "-";
        if (qty > 0)
          v += "COD:[" + qty + " " + cur + " " + (cod.postal ? "P": "N") + "] ";
      }
      let//
      ins = this.shipment.insurance;
      if (ins && ins.active) {
        let qty = ins.liability ? ins.liability.amount : 0;
        let cur = ins.liability && ins.liability.currency ? ins.liability.currency.code : "-";
        if (qty > 0)
          v += "INS:[" + qty + " " + cur + " " + (ins.fragile ? "F" : "N") + (ins.used ? "U" : "N") + "] ";
      }
      return v;
    },

    shipmentPaymentMethod() {
      return this.shipment.payment.method;
    },

    enablePackageRemoval() {
      return this.shipment.contents.length > 1;
    },

    serviceType() {
      // probably will be used for limitation of services.
      return '';
    },

    showTrackingProgress() {
      return !this.calculator && this.shipment && this.shipment.accepted;
    },

    packagingHandlingListAvailable() {
      if (this.shipment.contents.some(elem => elem.packaging.code !== 'EUS' && elem.packaging.code !== 'EU 1')) {// other than EUS and EU1 packaging selected in any content: restrict PackagingHandling to null
        this.shipment.packaging_handling = null;
        return [null];
      }
      return this.packagingHandlingList;
    },

    isCourierShipment() {
      if (typeof this.isCourierShipmentDeliveryDate === 'boolean') {
        return this.isCourierShipmentDeliveryDate;
      }
      else {
        return !(this.shipment.contents && this.shipment.contents.some(elem => !!Number(elem.weight)));
      }
    },

    isCityServiceType() {
      return (
           this.shipment.source.direct && this.shipment.target.direct
        && this.shipment.source.address && this.shipment.target.address
        && this.shipment.source.address.settlement && this.shipment.source.address.settlement.code
        && this.shipment.target.address.settlement && this.shipment.target.address.settlement.code
        && this.shipment.source.address.settlement.code == this.shipment.target.address.settlement.code
      )
    },

    isMaxDaysType() {
      return this.calendar.targetServicingType === 'max_days';
    },

    isInternationalShipment() {
      return (this.shipment.source && this.shipment.source.address && this.shipment.source.address.country && this.shipment.source.address.country.code !== "BG")
          || (this.shipment.target && this.shipment.target.address && this.shipment.target.address.country && this.shipment.target.address.country.code !== "BG");
    },

    isCourierShipmentCalculated() {
      const VWC = 150;
      let sumW = 0;
      this.shipment.contents.forEach(elem => {
        let v = elem.volume || 0,
            w = elem.weight || 0,
            x = elem.parcel_x || 0,
            y = elem.parcel_y || 0,
            z = elem.parcel_z || 0;
        sumW += Math.max(
          w,
          v * VWC,
          elem.count * x * y * z * VWC
        );
      });
      return sumW <= 31.5;
    },

    isComplexShipment() {
      return this.shipmentType.includes( "COMPLEX" );
    },

    isPersonalNumberRules() {// isPersonalNumberRequired
      // isCourierShipmentCalculated && has-COD && isInternationalShipment ...... was (isCourierShipmentCalculated AND is COD) OR isInternationalShipment
      if( !(
            (this.isCourierShipmentCalculated
            && this.shipment.cash_on_delivery && this.shipment.cash_on_delivery.cash_on_delivery && this.shipment.cash_on_delivery.amount && this.shipment.cash_on_delivery.amount.amount > 0)
            && this.isInternationalShipment
          )
        )
        return true;
      return this.shipment.source.personal_number && this.shipment.source.personal_number.trim() !== "";
    },

    courierShipmentText() {
      if (typeof this.isCourierShipment === 'boolean') {
          return this.isCourierShipment ? this.$t('Courier') : this.$t('Cargo');
      }
      return '';
    },

    personalNumberErrorMessages() {
      return this.isPersonalNumberRules ? "" : this.$t("Personal number.invalid");
    },

    isTabbed() {
      return this.screen && this.screen === "TABBED";
    },

    pricesItems() {
      return this.prices && this.prices.map(price => price.service_type) || [];
    },

    wmsOrder() {
      return this.$route.params.wms_order;
    },

    isOrderDisabled() {
      if (this.countries && this.countries.length
        && this.shipment.source && this.shipment.source.address && this.shipment.source.address.country && this.shipment.source.address.country.code
        && this.shipment.target && this.shipment.target.address && this.shipment.target.address.country && this.shipment.target.address.country.code
      ) {
        let src = this.countries.find(elem => elem.code === this.shipment.source.address.country.code);
        if (src && !src.is_allowed_orders) {
          return true;
        }
        let trg = this.countries.find(elem => elem.code === this.shipment.target.address.country.code);
        if (trg && !trg.is_allowed_orders) {
          return true;
        }
      }
      return false;
    },

    isShipmentReadonly() {
      return isShipmentReadonly(this.shipment);
    },

    connectedShipmentsDisabled() {
      return {
        return_doc: false,
        return_pod: false,
        return_pkg: !this.shipment.contents.some(elem => this.userOpts.packaging[elem.packaging.code] && this.userOpts.packaging[elem.packaging.code].returnable === true)
      }
    },
    // Sort cost centers to put user's preferred one first
    sortedCostCenters() {
      if (!this.costCenters.length)
        return [];

      const savedCostCenter = this.getSavedCostCenter();
      if (savedCostCenter && this.costCenters.includes(savedCostCenter)) {

        return [
          savedCostCenter,
          ...this.costCenters.filter(cc => cc !== savedCostCenter)
        ];
      }

      // Otherwise return original order from BE
      return this.costCenters;
    }
  },

  watch: {
    sourcePhoneRules() {
      // Vuetify 1 compares rule functions as objects and can miss changed closures.
      this.$nextTick(() => {
        if (this.$refs.sourcePhone) this.$refs.sourcePhone.validate();
      });
    },
    targetPhoneRules() {
      // Vuetify 1 compares rule functions as objects and can miss changed closures.
      this.$nextTick(() => {
        if (this.$refs.targetPhone) this.$refs.targetPhone.validate();
      });
    },

    "userInfo.options": {
      immediate: true,
      handler(options) {
        if (options)
          this.applyPmtOnCodOptions(options, false);
      }
    },
    id: {
      handler() {
        if (!this.loadDisabled)
          this.load();
        this.loadDisabled = false;
      }
    },

    poke(val) {
      if (val === true) {
        this.load();
        this.$emit("poked", true);
      }
    },

    validDelivery: {
      handler(val) {
        if (!!val) {
          return;
        }
        let vm = this;

        this.isCourierShipmentDeliveryDate = null;
        this.calendar.sourceDate = null;
        this.calendar.targetDate = null;

        if (!this.shipmentHasPriceParameters)
        {
          // console.log("Not enough parameters to calculate delivery");
          vm.validDelivery = true;
          return;
        }

        this.shipment.isCollectionDateChanged = !(
          !this.shipment.source.after || !this.shipmentStored.sourceAfter ||
          !this.shipment.source.until || !this.shipmentStored.sourceUntil ||
          !this.shipment.target.after || !this.shipmentStored.targetAfter ||
          !this.shipment.target.until || !this.shipmentStored.targetUntil ||
           this.shipment.source.after ==  this.shipmentStored.sourceAfter &&
           this.shipment.source.until ==  this.shipmentStored.sourceUntil &&
           this.shipment.target.after ==  this.shipmentStored.targetAfter &&
           this.shipment.target.until ==  this.shipmentStored.targetUntil
        );

        let src = this.sanitise();
        shipments.dates({}, src).then(response => {
          let result = response.body && response.body.result;
          if (result) {

            if (this.isInitiallyLoading) {
              this.isInitiallyLoading = false;
            }
            else {
              this.shipment.source.after = result.source.after;
              this.shipment.source.until = result.source.until;
              this.shipment.target.after = result.target.after;
              this.shipment.target.until = result.target.until;
            }

            // Store just loaded times
            this.shipmentStored = {
              sourceAfter: this.shipment.source.after,
              sourceUntil: this.shipment.source.until,
              targetAfter: this.shipment.target.after,
              targetUntil: this.shipment.target.until
            }

            // Calendar
            this.calendar.sourceTimeAfter = this.time2num( this.printDateTime(this.shipment.source.after, 2) );
            this.calendar.sourceTimeUntil = this.time2num( this.printDateTime(this.shipment.source.until, 2) );
            this.calendar.targetTimeAfter = this.time2num( this.printDateTime(this.shipment.target.after, 2) );
            this.calendar.targetTimeUntil = this.time2num( this.printDateTime(this.shipment.target.until, 2) );
            //
            this.calendar.sourceDate = this.printDateTime( this.shipment.source.after, 1);
            this.calendar.targetDate = this.printDateTime( this.shipment.target.until, 1);

            // Worktime & servicing schedule & Courier/Cargo
            this.setWorktime(result);
            //
            if (result.source.pickup_min_gap) {
              this.calendar.sourceTimeMinGap = result.source.pickup_min_gap;
            }
            //
            this.isCourierShipmentDeliveryDate = result.source.servicing.courier;// same duplicate in target; the flag is mistakenly put in .source
            //
            let sourceServicingCargo = this.defaultServicingSchedule();
            let sourceServicingCourier = this.defaultServicingSchedule();
            let targetServicingCargo = this.defaultServicingSchedule();
            let targetServicingCourier = this.defaultServicingSchedule();
            if (result.is_allow_not_serviced_settlements) {
              this.calendar.sourceServicing = sourceServicingCargo.week_days;
              this.calendar.targetServicing = targetServicingCargo.week_days;
            }
            else {
              sourceServicingCargo   = result.source.servicing.schedule.large;
              sourceServicingCourier = result.source.servicing.schedule.small;
              targetServicingCargo   = result.target.servicing.schedule.large;
              targetServicingCourier = result.target.servicing.schedule.small;
              // TODO: null checks
              //Source servicing
              let scheduleCalendar = result.source.servicing.courier ? sourceServicingCourier : sourceServicingCargo;
              if (scheduleCalendar.type === "week_days") {
                this.calendar.sourceServicing = [...scheduleCalendar.week_days];
                this.calendar.sourceServicing.unshift( this.calendar.sourceServicing.pop() );
              } else {
                // TODO: max_days - what shall we show in the calendar?
                this.calendar.sourceServicing = this.defaultServicingSchedule().week_days;
              }
              //Target servicing
              scheduleCalendar = result.target.servicing.courier ? targetServicingCourier : targetServicingCargo;
              if (scheduleCalendar.type === "week_days") {
                this.calendar.targetServicing = [...scheduleCalendar.week_days];
                this.calendar.targetServicing.unshift( this.calendar.targetServicing.pop() );
              } else {
                this.calendar.targetServicing = this.defaultServicingSchedule().week_days;
              }
              this.calendar.targetServicingType = scheduleCalendar.type;
            }

            setTimeout(() => {
              postProcessMessageDOM(this, {
                servicing: { courier: sourceServicingCourier, cargo: sourceServicingCargo },
                isCourier: this.isCourierShipment
              }, 'calendarSourceDate', (result.is_allow_not_serviced_settlements && 'primary--text text--lighten-2 font-weight-bold'));
              postProcessMessageDOM(this, {
                servicing: { courier: targetServicingCourier, cargo: targetServicingCargo },
                isCourier: this.isCourierShipment
              }, 'calendarTargetDate', (result.is_allow_not_serviced_settlements && 'primary--text text--lighten-2 font-weight-bold'));
            }, 500);

            // Order date != Pickup date
            if (!this.isSameDay(this.shipment.order_date || new Date().getTime(), this.shipment.source.after)) {
              this.msg( this.$t('The pickup date is not the same date as order!'), 'warning', 6000 );
            }
          }

        }).catch(error => {
          console.log("Error with delivery dates", error);
        }).finally(() => {
          this.validDelivery = true;
        });
      }
    },

    validPrice: {
      handler(val) {
        // only if price is invalid
        let vm = this;
        if (this.isShipmentReadonly) {
          vm.validPrice = true;
        }
        if (val) return;
        if (!this.shipmentHasPriceParameters)
        {
          // console.log("Not enough price parameters");
          let p = vm.shipment.payment.price;
          if (p)
          {
            if (p.price)
              p.price.amount = 0;
            if (p.price_with_tax)
              p.price_with_tax.amount = 0;
          }

          vm.validPrice = true;
          return;
        }

        let src = this.sanitise();

        shipments.price({}, src).then(response => {
          if (response.body.state === 'fail') {
            this.priceFail(response.body.description);
          }
          let prices = response.body.result;

          let ship_serv_type = vm.shipment.service.type;
          if (ship_serv_type === 'COURIER_CITY_ECONOMIC') {
              ship_serv_type = 'ECONOMIC';
          }

          let price = prices.find(elem => elem.service_type === ship_serv_type);
          if (price) {
            if (price.price) {
              vm.shipment.payment.price.price = price.price;
            }
            if (price.price_tax) {
              vm.shipment.payment.price.price_with_tax = price.price_tax;
            }
          }

          vm.prices = [];
          prices.forEach(elem => {
            let pr = {
              service_type: elem.service_type,
              price:        elem.price,
              price_tax:    elem.price_tax,
              components: {}
            }
            elem.components.forEach(comp => {
              let d = (comp.comment === "FUEL_TAX" || comp.comment === "TRANSPORT")
                ? pr
                : pr.components;

              d[comp.comment] = {
                amount: comp.amount.amount,
                currency: comp.amount.currency,
                info: comp.info
              }
            });
            if (pr.service_type === ship_serv_type)
              vm.prices.unshift( pr );
            else
              vm.prices.push( pr );
          });

        }).catch(function(err) {
          console.log("Cannot calculate price!", err);
        }).finally(() => {
          vm.validPrice = true;
        });
      }
    },

    shipmentSourceAddress: {
      handler: function(val, oldVal) {
        // console.log("Invalid PRICE & Delivery: Source address changed! " + oldVal + " => " + val);
        this.invalidatePrice(true);
      }
    },

    shipmentTargetAddress: {
      handler: function(val, oldVal) {
        // console.log("Invalid PRICE & Delivery: Target address changed! " + oldVal + " => " + val);
        this.invalidatePrice(true);
      }
    },

    shipmentParcelsWeight: {
      handler: function(val, oldVal) {
        // console.log("Invalid PRICE & Delivery: Parcels/Weight changed! " + oldVal + " => " + val);
        this.invalidatePrice(true);
      }
    },

    shipmentServicesOptions: {
      handler: function(val, oldVal) {
        // console.log("Invalid PRICE & Delivery: Service options changed! " + oldVal + " => " + val);
        this.invalidatePrice(true);
      }
    },

    shipmentAdditionalOptions: {
      handler: function(val, oldval) {
        // console.log("Invalid PRICE, Additional options changed: " + oldval + " => " + val);
        this.invalidatePrice(false);
      }
    },

    shipmentPaymentMethod: {
      handler: function(val, oldval) {
        if (val === "PAYED_ON_DELIVERY")
          this.shipment.payment.cash = true;
        else
          this.shipment.payment.cash = false;
      }
    },

    isCityServiceType: {
      handler: function(val) {
        this.setServiceTypeDefaults();
      }
    },

    calculator(val) {
      if (val) {
        this.resetScreenModesData();
      }
      else {
        setTimeout( () => this.initScreenModes(), 500 );// TODO: find a way to ensure elems are loaded
      }
    },

    "screenModesData.expanel"(val) {
      this.storePreferences("screen");
    },

    "shipmentType"(val) {
      if (this.isComplexShipment) {
        this.shipment.source.direct = true;
        this.shipment.target.direct = true;
      }

      this.shipment.type = this.isComplexShipment ? "COMPLEX" : "NETWORK";
      this.shipment.source.network = val === "COMPLEX-NETWORK-FROM";
      this.shipment.target.network = val === "COMPLEX-NETWORK-TO";

      this.station.source.disabled = val !== "COMPLEX-NETWORK-TO";
      this.station.target.disabled = val !== "COMPLEX-NETWORK-FROM";
    },
    "shipment.source.address.settlement.code"(val) {
      if (val && !this.isSameSettlementAsStored(val, "source")) {
        this.stationRequest( val, "source" );
      }
      this.setServiceType();
    },
    "shipment.target.address.settlement.code"(val) {
      if (val && !this.isSameSettlementAsStored(val, "target")) {
        this.stationRequest( val, "target" );
      }
      this.setServiceType();
    },

    "shipment.source.personal_number"(val) {
      this.storePreferences("personalNumber");
    },

    'calendar.sourceTime'(isOpen) {
      if (isOpen) {
        this.calendar.sourceTimeAfter = this.time2num( this.printDateTime(this.shipment.source.after, 2) );
        this.calendar.sourceTimeUntil = this.time2num( this.printDateTime(this.shipment.source.until, 2) );

        if (this.isToday(this.calendar.sourceDate)) {

          let setCalendarTimeNow = () => {
            let now = new Date();
            this.calendar.timeNow = Number(now.getHours() + '' + ('0' + now.getMinutes()).slice(-2));
          }
          setCalendarTimeNow();
          this.calendar.timeNowInterval = setInterval(() => {
            setCalendarTimeNow();
          }, 10000);
        }
        else {
          clearInterval(this.calendar.timeNowInterval);
        }
      }
      else {
        clearInterval(this.calendar.timeNowInterval);
      }
    },
    'calendar.targetTime'(isOpen) {
      if (isOpen) {
        this.calendar.targetTimeAfter = this.time2num( this.printDateTime(this.shipment.target.after, 2) );
        this.calendar.targetTimeUntil = this.time2num( this.printDateTime(this.shipment.target.until, 2) );
      }
    },
    isOrderDisabled(val) {
      if (val) {
        this.msg(
          this.$t('WARNING.COUNTRY.ORDERS_DISALLOWED'),
          'warning',
          0
        );
      }
    },

    'requestPrefs': {
      handler: function(val) {
        let prefs = JSON.parse(this.requestPrefs);
        if (prefs && prefs.units) {
          Object.keys(prefs.units).forEach(key => {
            this.units[key] = prefs.units[key];
          });
        }
      },
      deep: true
    },
    costCenters: {
      handler(newCenters) {
        if (newCenters && newCenters.length > 0 && !this.shipment.cost_center) {
          const savedCostCenter = this.getSavedCostCenter();

          // Use saved preference if valid, otherwise use first
          if (savedCostCenter && newCenters.includes(savedCostCenter)) {
            this.shipment.cost_center = savedCostCenter;
          } else {
            this.shipment.cost_center = newCenters[0];
          }
        }
      },
      immediate: true
    }
  },

  methods: {
    hasAdr,
    adrAllowed(parcel, packaging) {
      const selected = packaging || this.userOpts.packaging[parcel && parcel.packaging && parcel.packaging.code];
      return allowsAdr(this.shipment, selected);
    },
    adrError(parcel, idx) {
      if (hasAdr(parcel) && !this.adrAllowed(parcel))
        return this.$t(ADR_PALLET_MESSAGE);
      return this.errors(['parcelSets[' + idx + '].dangerous', 'contents[' + idx + '].dangerous_goods'], 3);
    },
    acceptAdrPackaging(packaging, parcel, idx) {
      if (hasAdr(parcel) && !allowsAdr(this.shipment, packaging)) {
        this.msg(this.$t(ADR_PALLET_MESSAGE), 'error');
        return false;
      }
      this.clearErrors(['parcelSets[' + idx + '].dangerous', 'contents[' + idx + '].dangerous_goods']);
      return true;
    },
    openDangerousGoods(parcel) {
      if (!this.adrAllowed(parcel)) {
        this.msg(this.$t(ADR_PALLET_MESSAGE), 'error');
        // Existing details must remain accessible so the user can remove them.
        if (!hasAdr(parcel)) return;
      }
      this.activeAdrParcel = parcel;
      this.dangerousGoodsShow = true;
    },
    setDangerousGoods(goods) {
      if (!this.activeAdrParcel) return;
      if (goods.length && !this.adrAllowed(this.activeAdrParcel)) {
        this.msg(this.$t(ADR_PALLET_MESSAGE), 'error');
        return;
      }
      this.$set(this.activeAdrParcel, 'dangerous_goods', goods);
      const idx = this.shipment.contents.indexOf(this.activeAdrParcel);
      this.clearErrors(['parcelSets[' + idx + '].dangerous', 'contents[' + idx + '].dangerous_goods']);
    },
    validateAll() {
      const formsValid = ['form1', 'form2', 'form3', 'form4'].map(name => {
        const form = this.$refs[name];
        return !!form && form.validate();
      }).every(Boolean);
      const adrValid = this.shipment.contents.every(parcel => !hasAdr(parcel) || this.adrAllowed(parcel));
      if (!adrValid) this.step = 3;
      return formsValid && adrValid;
    },

    ...mapActions(["loadUserInfo", "updateRequestPrefs"]),


    defaultServicingSchedule() {
      const DEF_SERVICING = [ false, true, true, true, true, true, false ];
      return {
        "type": "week_days",
        "week_days": [...DEF_SERVICING]
      };
    },

    resetScreenModesData() {
      this.screenModesData.disabled = true;
      // this.screen = this.screenModes[0]; // Thought is it required to be (visible in DOM) mode TABBED to properly initialize!?
      let myPrefs = this.requestPrefs ? JSON.parse(this.requestPrefs) : null;
      this.screenModesData.expanel = (myPrefs && myPrefs.screen && myPrefs.screen.expanel) ? myPrefs.screen.expanel : [true, true, true, true];
      this.screenModesData.stepperContainers = null;
      this.screenModesData.expanelContainers = null;
    },

    initScreenModes() {
      this.screenModesData.contents = [];
      // Contents: form1-4
      for (let i=1; i <= 4; i++) {
        let form = this.$refs["form" + i];
        if (form)
          this.screenModesData.contents.push( form.$el );
      }
      // Wrappers
      this.screenModesData.stepperContainers = document.querySelectorAll(".v-stepper__wrapper");// isTabs TABBED <stepper>
      this.screenModesData.expanelContainers = document.querySelectorAll(".v-expansion-panel__body .v-card__text");// !isTabs SINGLE_PAGE <expansion-panel>

      this.screenModesData.disabled = false;

      if (this.screen !== this.screenModes[0])
        this.toggleScreenModes();
    },

    toggleScreenModes() {
      if (!this.screenModesData.contents || this.screenModesData.contents.length === 0 || !this.screenModesData.stepperContainers || !this.screenModesData.expanelContainers) {
        return;
      }
      this.screenModesData.contents.forEach((elem, i) => {

        if (this.isTabbed && this.screenModesData.stepperContainers[i].childNodes.length === 0)
          this.screenModesData.stepperContainers[i].appendChild( elem );

        else if (!this.isTabbed && this.screenModesData.expanelContainers[i].childNodes.length === 0)
          this.screenModesData.expanelContainers[i].appendChild( elem );
      });

      this.storePreferences("screen");
    },

    printDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleString();
    },

    printDateTime(datetime, dateOrTime) {
      if (!datetime) return '';
      let d = new Date( datetime ),
          year  = d.getFullYear(),
          month = ('0' + (d.getMonth()+1) ).slice(-2),
          day   = ('0' + d.getDate() ).slice(-2),
          hour  = ('0' + d.getHours() ).slice(-2),
          min   = ('0' + d.getMinutes() ).slice(-2);

      if (dateOrTime && dateOrTime == 1)
        return `${year}-${month}-${day}`;
      else if (dateOrTime && dateOrTime == 2)
        return `${hour}:${min}`;
      else
        return `${year}-${month}-${day} ${hour}:${min}`;
    },

    isSameDay(d1, d2) {
      if (!d1 || !d2)
        return;
      d1 = new Date(d1);
      d2 = new Date(d2);
      return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
      );
    },

    isToday(date) {
      return this.isSameDay( new Date(date), new Date() );
    },

    dayOfWeek(date) {
      if (!date) {
        return '';
      }
      return this.$t('dayOfWeek.' + new Date(date).getDay() + '.two');
    },

    allowedSourceDates(val) {
      let d = new Date(val);
      if (! this.calendar.sourceServicing[ d.getDay() ])
        return false;
      let now = new Date();
      if (new Date( d.setHours(0,0,0,0) ) < new Date( now.setHours(0,0,0,0) ))
        return false;
      return true;
    },

    currencySymbol(amount, prefix) {
      if (!amount || !amount.currency || !amount.currency.symbol)
        return '';

      if (prefix !== amount.currency.prefix)
        return '';

      return amount.currency.symbol;
    },

    allowedTargetDates(val) {
      let d = new Date(val);
      if (! this.calendar.targetServicing[ d.getDay() ])
        return false;
      let now = new Date();
      if (new Date( d.setHours(0,0,0,0) ) < new Date( now.setHours(0,0,0,0) ))
        return false;

      // ECONOMIC service type: don't allow dates less than 2 days from now
      if (this.shipment.service && this.shipment.service.type === 'ECONOMIC') {
        let workDays = 0;
        let current = new Date(this.calendar.sourceDate);
        current.setHours(0, 0, 0, 0);
        while (workDays < 2) {
          current.setDate(current.getDate() + 1);
          if (!this.calendar.targetServicing[ current.getDay() ])
            continue;
          workDays++;
        }
        if (d < current)
          return false;

      }
      return true;
    },

    nextAllowedSourceDate(datetime) {
      for (let i=1; i<31; i++) {
        if (this.allowedSourceDates( datetime )) {
          break;
        }
        let d = new Date(datetime);
        d.setDate(d.getDate() + 1);
        datetime = d.valueOf();
      }
      return datetime;
    },

    nextAllowedTargetDate(datetime) {
      for (let i=1; i<31; i++) {
        if (this.allowedTargetDates( datetime )) {
          break;
        }
        let d = new Date(datetime);
        d.setDate(d.getDate() + 1);
        datetime = d.valueOf();
      }
      return datetime;
    },

    time2str(num) {
      if (!num || !Number(num)) {
        return null;
      }
      num = Number(num);
      return ('0' + Math.floor( num/100 )).slice(-2) + ':' + ('0' + num % 100).slice(-2);
    },
    time2num(str) {
      if (!str) {
        return null;
      }
      return Number(str.replace(/:/, ''));
    },
    timeTo100(num) {
      return Math.floor(num / 100) + (num % 100) / 60;
    },

    calendarTimesClass(value, isSource) {
      let clas = '';
      if (!this.isCityServiceUserTime) {
        clas += (value > this.calendar[isSource ? 'sourceTimeAfter' : 'targetTimeAfter'] && value < this.calendar[isSource ? 'sourceTimeUntil' : 'targetTimeUntil'] ? '  primary lighten-5' : '');
      }
      clas += (value % 100 ? '' : ' grey lighten-4');
      return clas;
    },

    /** Outputs an array of objs `{ value: 730, text: "07:30" }` for Time Range Widget; wobj = worktimeObject = `{ after: Number, until: Number }` */
    calendarTimeItems(wobj) {
      // min = 900;
      let min = wobj.after;

      // max = 1645;
      let max = wobj.until;
      if (this.isCityServiceUserTime) {
        max = 1530;
      }

      const MINUTE_STEP = 30;
      const timesInHour = 60 / MINUTE_STEP;

      let times = [];

      hourFor:
      for (var h=Math.floor(min/100); h < 24; h++) {
        for (var m=0; m < timesInHour; m++) {
          let num = h*100 + m*MINUTE_STEP;
          if (num > max) {
            break hourFor;
          }
          times.push({
            value: num,
            text: ('0' + h).slice(-2) + ':' + ('0' + m*MINUTE_STEP).slice(-2)
          });
        }
      }
      return times;
    },

    calendarTargetTimeItems() {
      let times = [];

      // 10:00, 12:00, 14:00
      let min = this.calendar.targetWorktime.after;// this.calendar.targetTimeAfter
      if (min <= 1000) {
        times.push({ value: 1000, text: '10:00' });
      }
      if (min <= 1200) {
        times.push({ value: 1200, text: '12:00' });
      }
      if (min <= 1400) {
        times.push({ value: 1400, text: '14:00' });
      }

      min = min <= 1600 ? 16 : Math.floor(min/100);
      let max = this.calendar.targetWorktime.until;

      // 16:30 - worktime.until
      const MINUTE_STEP = 30;
      const timesInHour = 60 / MINUTE_STEP;

      hourFor:
      for (var h=min; h < 24; h+=1) {
        for (var m=0; m < timesInHour; m++) {
          let num = h*100 + m*MINUTE_STEP;
          if (num > max) {
            break hourFor;
          }
          times.push({
            value: num,
            text: ('0' + h).slice(-2) + ':' + ('0' + m*MINUTE_STEP).slice(-2)
          });
        }
      }
      return times;
    },

    calendarSourceChanged() {
      let sourceAfterAllowed = this.nextAllowedSourceDate( new Date(this.calendar.sourceDate).getTime() );
      this.calendar.sourceDate = this.printDateTime( sourceAfterAllowed, 1 );

      let isToday = this.isToday( this.calendar.sourceDate );

      this.shipment.source.after = new Date(this.calendar.sourceDate + 'T' + ( isToday ? this.printDateTime(this.shipment.source.after, 2) : this.time2str(this.calendar.sourceWorktime.after) )).getTime();
      this.shipment.source.until = new Date(this.calendar.sourceDate + 'T' + ( isToday ? this.printDateTime(this.shipment.source.until, 2) : this.time2str(this.calendar.sourceWorktime.until) )).getTime();

      if (this.shipment.source.after > this.shipment.source.until)
          this.shipment.source.after = this.shipment.source.until - 1;

      this.calendar.source = false;
      this.invalidatePrice(true);
    },

    calendarTargetChanged() {
      let targetAfterAllowed = this.nextAllowedTargetDate( new Date(this.calendar.targetDate).getTime() );
      this.calendar.targetDate = this.printDateTime( targetAfterAllowed, 1 );

      this.shipment.target.after = new Date(this.calendar.targetDate + 'T' + this.printDateTime(this.shipment.target.after, 2 )).getTime();
      this.shipment.target.until = new Date(this.calendar.targetDate + 'T' + this.printDateTime(this.shipment.target.until, 2 )).getTime();

      if (this.shipment.target.after > this.shipment.target.until)
          this.shipment.target.after = this.shipment.target.until - 1;

      this.calendar.target = false;
      this.invalidatePrice(true);
    },

    calendarSourceTimesChanged() {
      this.shipment.source.after = new Date(this.calendar.sourceDate + 'T' + this.time2str(this.calendar.sourceTimeAfter)).getTime();
      this.shipment.source.until = new Date(this.calendar.sourceDate + 'T' + this.time2str(this.calendar.sourceTimeUntil)).getTime();
      this.calendar.sourceTime = false;
      this.invalidatePrice(true);
    },

    calendarTargetTimesChanged() {
      let targetDate = this.printDateTime(this.shipment.target.after, 1);

      this.shipment.target.after = new Date(targetDate + 'T' + this.time2str(this.calendar.targetTimeAfter)).getTime();
      this.shipment.target.until = new Date(targetDate + 'T' + this.time2str(this.calendar.targetTimeUntil)).getTime();
      this.calendar.targetTime = false;
      this.invalidatePrice(true);
    },

    setWorktime(result) {

      {// source
        let sworktime = result.source.worktime;

        if (this.shipment.source.direct) {
          this.calendar.sourceWorktime = this.sourceWorktimeComputed({
            after: sworktime.service_after,
            until: sworktime.contract_latest_time || sworktime.service_until
          });
        }
        else if (this.shipment.source.after) {
          // Insert enpoint calculating wheather a given date is a workday
          shipments.workdate({}, {
            date: new Date(this.shipment.source.after).getTime()
          }).then(res => {
            if (res.body && typeof res.body.workday !== 'undefined') {

              this.calendar.sourceWorktime = this.sourceWorktimeComputed({
                after: res.body.workday ? sworktime.workday_after : sworktime.holiday_after,
                until: res.body.workday ? sworktime.workday_until : sworktime.holiday_until
              });
            }
          }).catch(err => {
            console.log('Error /customer/centre/day-classification.svc', err);
          });
        }

        // sourceTimeUntilIsBefore14
        this.calendar.sourceTimeUntilIsBefore14 = (
          sworktime.contract_latest_time || sworktime.service_until || 1800
        ) < 1400;
      }

      {// target
        let tworktime = result.target.worktime;

        if (this.shipment.target.direct) {
          this.calendar.targetWorktime = this.targetWorktimeComputed({
            after: tworktime.service_after,
            until: tworktime.service_until
          });
        }
        else if (this.shipment.target.after) {
          // Insert enpoint calculating wheather a given date is a workday
          shipments.workdate({}, {
            date: new Date(this.shipment.target.after).getTime()
          }).then(res => {
            if (res.body && typeof res.body.workday !== 'undefined') {

              this.calendar.targetWorktime = this.targetWorktimeComputed({
                after: res.body.workday ? tworktime.workday_after : tworktime.holiday_after,
                until: res.body.workday ? tworktime.workday_until : tworktime.holiday_until
              });
            }
          }).catch(err => {
            console.log('Error /customer/centre/day-classification.svc', err);
          });
        }
      }

      {// Enable/disable Speed 10-12-14 services
        [14, 12, 10].forEach(elem => {
          const time = elem * 100;
          const name = 'EXPRESS_' + elem;

          this.userOpts.shipmentServicesAllowed[name] = !(
              this.calendar.targetWorktime.after && this.calendar.targetWorktime.after >= time
            ||this.calendar.targetWorktime.until && this.calendar.targetWorktime.until <  time
          );
        });
      }
    },

    sourceWorktimeComputed(worktime) {
      let after = worktime.after ||  900;
      let until = worktime.until || 1800;

      let end = new Date();
      end.setHours( Math.floor( until/100 ), until % 100 );
      end.setMinutes( end.getMinutes());// - this.serviceDurationMins );

      return {
        after: Number(after),
        until: Number(end.getHours() + ('0' + end.getMinutes()).slice(-2))
      };
    },

    targetWorktimeComputed(worktime) {
      let after = worktime.after ||  900;
      let until = worktime.until || 1800;

      return {
        after: Number(after),
        until: Number(until)
      };
    },

    showTracking(id) {
      if (!id) return;
      this.$router.push({ name: "track_id", params: { id }});
    },

    sourcePartnerChanged(val) {
      let t = this.shipment;
      if (t && t.source && t.source.partner && t.source.partner.code)
        this.shipment.source.partner.code = null;
    },

    invalidatePriceImpl(delivery) {
      this.validPrice = false;
      if (delivery)
        this.validDelivery = false;
    },

    resizePriceBoxImpl() {
      if (this.$vuetify.breakpoint.xs)
      { // small screen
        if (this.priceBox.icon.startsWith('chevron'))
        {
          this.priceBox.icon = (this.priceBox.icon === 'chevron_left') ?
            'expand_less' : 'expand_more';
        }
      }
      else
      { // large screen
        if (this.priceBox.x === 'right' && this.priceBox.y === 'bottom')
          this.priceBox.icon = 'chevron_left';
        if (this.priceBox.x === 'left' && this.priceBox.y === 'top')
          this.priceBox.icon = 'chevron_right';
      }
    },

    nextPriceBox(what) {
      if (what === 'float')
      {
        this.priceBox.float = !this.priceBox.float;
      }
      // position change
      if (what === 'pos')
      {
        // vertical movement - for smallest screens
        if (this.$vuetify.breakpoint.xs)
        {
          this.priceBox.y = this.priceBox.y === 'bottom' ? 'top' : 'bottom';
          this.priceBox.icon = (this.priceBox.icon === 'expand_more' || this.priceBox.icon === 'chevron_left') ? 'expand_less' : 'expand_more';
        }
        else if (this.priceBox.y === 'bottom')
        {
          if (this.priceBox.x === 'left')
          {
            this.priceBox.y = 'top';
            this.priceBox.icon = 'chevron_right';
          }
          else
          {
            this.priceBox.x = 'left';
            this.priceBox.icon = 'expand_less';
          }
        }
        else
        {
          if (this.priceBox.x === 'left')
          {
            this.priceBox.x = 'right';
            this.priceBox.icon = 'expand_more';
          }
          else
          {
            this.priceBox.y = 'bottom';
            this.priceBox.icon = 'chevron_left';
          }
        }
      }
      this.storePreferences('priceBox');
    },

    // clean up shipment data before actual save
    sanitise() {
      let s = JSON.parse(JSON.stringify(this.shipment));
      for (let x = 0; x < s.contents.length; x++)
      {
        let p = s.contents[x];
        delete p.dimXEditable;
        delete p.dimYEditable;
        delete p.dimZEditable;
        p.count = toNumber(p.count);
        p.weight = toNumber(p.weight);
        p.volume = toNumber(p.volume);
        p.parcel_x = toNumber(p.parcel_x);
        p.parcel_y = toNumber(p.parcel_y);
        p.parcel_z = toNumber(p.parcel_z);
        let dgs = p.dangerous_goods;
        if (!!dgs && !!dgs.length)
          dgs.forEach(dg => {
            dg.weight = toNumber(dg.weight);
            dg.packages_number = toNumber(dg.packages_number);
          });
      }
      if (s.cash_on_delivery && s.cash_on_delivery.amount && s.cash_on_delivery.amount.amount)
        s.cash_on_delivery.amount.amount = toNumber(s.cash_on_delivery.amount.amount);
      if (s.cash_on_delivery && s.cash_on_delivery.cash_on_delivery && !s.cash_on_delivery.payment_type)
        s.cash_on_delivery.payment_type = 'CASH';
      if (s.insurance && s.insurance.liability && s.insurance.liability.amount)
        s.insurance.liability.amount = toNumber(s.insurance.liability.amount);

      if (s.source && s.source.partner) {
        let c = s.source.partner.code;
        if (c === null && !c && typeof c === 'object') {
          delete s.source.partner.code;
        }
      }
      if (s.target && s.target.partner) {
        let c = s.target.partner.code;
        if (c === null && !c && typeof c === 'object') {
          delete s.target.partner.code;
        }
      }

      if (s.source && !s.source.direct) {
          if (s.source.address && s.source.address.settlement && s.source.address.settlement.code === null) {
            s.source.address.settlement.name = null;
          }
          s.source.address = getDefaultShipment().source.address;
          if (s.type === "NETWORK")
            s.source.station = null;
      }
      if (s.target && !s.target.direct) {
          if (s.target.address && s.target.address.settlement && s.target.address.settlement.code === null) {
            s.target.address.settlement.name = null;
          }
          if (s.type === "NETWORK")
            s.target.station = null;
      }

      // should office be clean also (on direct)?
      if (s.source.direct) {
        s.source.office = getDefaultShipment().source.office;
      }
      else {
        s.source.address = getDefaultShipment().source.address;
      }
      if (s.target.direct) {
        s.target.office = getDefaultShipment().target.office;
      }
      else {
        s.target.address = getDefaultShipment().target.address;
      }

      if (s.source && s.source.station)
        delete s.source.station.address;
      if (s.source && s.source.office)
        delete s.source.office.address;

      if (s.target && s.target.station)
        delete s.target.station.address;
      if (s.target && s.target.office)
        delete s.target.office.address;

      if (s.source && s.source.address && s.source.address.settlement) {
        delete s.source.address.settlement.client_message;
        delete s.source.address.settlement.delivery_days_cargo;
        delete s.source.address.settlement.delivery_days_courier;
      }
      if (s.target && s.target.address && s.target.address.settlement) {
        delete s.target.address.settlement.client_message;
        delete s.target.address.settlement.delivery_days_cargo;
        delete s.target.address.settlement.delivery_days_courier;
      }

      delete s.progress;
      delete s.adr_packaging_exempt;

      return s;
    },

    errors(key, pane, elem) {
      if (Array.isArray(key))
      {
        let s = "";
        for(let x = 0; x < key.length; x++)
        {
          let m = this.errors(key[x], pane);
          if (m)
          {
            if (s.length > 0) s = s + ", ";
            s = s + m;
          }
        }
        return s;
      }

      let//
      msg = this.errorMessages.get(key);
      if (!!msg)
      { // mark message as consumed, so that to not be displayed as common error message
        // save the pane that have consume it, in order to mark panes are invalid
        this.errorConsumed.set(key, pane);
        this.errorDetails = true;
      }
      else if (key.includes("parcelSets")) {// check format compatibility

        let elem = key.replace(/parcelSets/, "contents");
        let elemVal = eval("this.shipment." + elem.toLowerCase());
        let field = key.substr(key.lastIndexOf('.') + 1);

        if (typeof elemVal !== "undefined" && !elemVal.toString()) {
          return this.$t("Not a number");
        }
        if (field === "count" && elemVal <= 0) {
          return this.$t("This field is required!");
        }
        if ((field === "weight" || field === "volume") && elemVal < 0) {
          return this.$t("Incorrect value");
        }
        if (field === "parcel_Z" && elemVal > 2.2) {
          return this.$t('Height max value');
        }

        if (!!field.match(/parcel_[XYZ]/)) {
          let elemPackaging = elem.replace(/parcel_[XYZ]/, "packaging"); // 'parcelSets->contents[0].parcel_X', 3
          let elemPackagingCode = eval("this.shipment." + elemPackaging.toLowerCase() + ".code");
          if (
              (elemPackagingCode === "NSTD" || elemPackagingCode === "КАШ1" || elemPackagingCode === "ST")
              && elemVal <= 0
            )
              return this.$t("This field is required!");
          else if (
              field === "parcel_Z"
              && (this.userOpts.packaging[elemPackagingCode] && this.userOpts.packaging[elemPackagingCode].standard)
              && elemVal <= 0
            )
              return this.$t('This field is required!');
        }
      }

      return msg;
    },

    clearErrors(key) {
      if (Array.isArray(key))
      {
        for(let i = 0; i < key.length; i++)
          this.clearErrors(key[i]);
        return;
      }

      if (this.errorMessages.has(key))
      {
        this.errorMessages.delete(key);
        this.errorMessagesVersion ++;
      } else
      { // clear family of errors
        let c = 0;
        this.errorMessages.forEach( (v,k) =>
        {
          if(k.startsWith(key))
          {
            c++;
            this.errorMessages.delete(k);
          }
        });
        if (c > 0)
        {
          this.errorMessagesVersion ++;
        }
      }
      this.errorDetails = this.errorMessages.size > 0;
    },

    displayErrors() {
      let s = "";
      this.errorMessages.forEach( (v, k) => {
        if (!this.errorConsumed.has(k))
        {
          if (s.length>0) s += ", ";
          s = s + v;
          console.log("ERROR NOT HANDLED: " + v + " => " + k);
        }
      });
      if (!s)
        s = this.$t("Please fix errors before continuing!");
      return this.errorMessagesVersion && s;
    },

    processErrors(errors) {
      this.storePreferences("source");
      this.errorDetails = false;
      this.errorMessages.clear();
      this.errorConsumed.clear();
      this.errorMessagesVersion = 1;
      if (!!errors && errors.length)
      {
        let c = 0;
        for (let x = 0 ; x < errors.length; x ++)
        {
          let k = errors[x].path;
          let v = errors[x].text;
          if (this.errorMessages.has(k))
          {
            let o = this.errorMessages.get(k);
            v = o + "! " + v;
          }
          this.errorMessages.set(k, v);
          c++;
        }
        if(c)
        {
          this.errorMessagesVersion++;
          this.errorDetails = true;
        }
      }
    },

    gotoStep(ev) {
      let s = 1;
      switch(ev.srcKey) {
        case 's1' :
          s = 1;
          break;
        case 's2' :
          s = 2;
          break;
        case 's3' :
          s = 3;
          break;
        case 's4' :
          s = 4;
          break;
      }

      console.log("Goto step " + s + " from step: " + this.step);
      this.step = s;
    },

    repairBackendData() {
      if (this.shipment && this.shipment.contents && Array.isArray(this.shipment.contents)) {
        for(let x = 0; x < this.shipment.contents.length; x++) {
          if (!this.shipment.contents[x].packaging)
            this.shipment.contents[x].packaging = { code : "", name : "" };
        }
      }
      let p = this.shipment.source;
      if (p && ((p.partner && !p.partner.code) || (!p.partner))) {
        p.partner = { code : null, name : "" };
      }
      if (p && !p.personal_number)
        p.personal_number = "";

      p = this.shipment.target;
      if (p && ((p.partner && !p.partner.code) || (!p.partner))) {
        p.partner = { code : null, name : "" };
      }
      if (this.shipment && this.shipment.payment && !this.shipment.payment.price) {
        this.shipment.payment.price = {};
        this.shipment.payment.price.price = { amount: 0, currency: { code: "EUR" }};
        this.shipment.payment.price.price_with_tax = { amount: 0, currency: { code: "EUR" }};
      }
      if (this.shipment && !this.shipment.cash_on_delivery) {
        this.shipment.cash_on_delivery = {
          cash_on_delivery: false,
          paid: false,
          postal: false,
          amount: {
            amount: null,
            currency: {
              code: "EUR",
              symbol: "€",
              prefix: true
            }
          },
          payment_type: null
        }
      }
      else if (this.shipment && this.shipment.cash_on_delivery && !this.shipment.cash_on_delivery.amount)
      {
        this.shipment.cash_on_delivery.amount = {
          amount: null,
          currency: {
            code: "EUR",
            symbol: "€",
            prefix: true
          }
        }
      }
      if (this.shipment && !this.shipment.insurance) {
        this.shipment.insurance = {
          active: false,
          liability: {
            amount: null,
            currency: {
              code: "EUR",
              symbol: "€",
              prefix: true
            }
          },
          fragile: false,
          used: false
        }
      } else if (this.shipment && this.shipment.insurance && !this.shipment.insurance.liability) {
        this.shipment.insurance.liability = {
          amount: null,
          currency: {
            code: "EUR",
            symbol: "€",
            prefix: true
          }
        }
      }

      if (this.shipment && this.shipment.type)
      {
        if (this.shipment.type === "NETWORK")
          this.shipmentType = this.shipment.type;
        else
        {
          this.shipmentType = "COMPLEX";
          if (!!this.shipment.source && !!this.shipment.target)
          {
            if (this.shipment.source.network && !this.shipment.target.network)
              this.shipmentType = "COMPLEX-NETWORK-FROM";
            else
            if (!this.shipment.source.network && this.shipment.target.network)
              this.shipmentType = "COMPLEX-NETWORK-TO";
          }
        }
      }
    },

    load() {
      this.loading = true;

      if (this.id > 0) {
        shipments.load({ id: this.id }).then((result) => {
          this.shipment = result.body;
          if (this.$route.params.IS_CLONE) {
            this.shipment.adr_packaging_exempt = false;
            this.shipment.id = 0;
            this.shipment.manifest = "";
            this.shipment.accepted = false;
            this.shipment.collected = false;
            this.shipment.cancellable = false;
            this.shipment.reference = null;//?
            this.shipment.order_date = null;//?
            this.shipment.owned = true;
            this.shipment.source.after = null;
            this.shipment.source.until = null;
            this.shipment.target.after = null;
            this.shipment.target.until = null;
          }
          else {
            this.isInitiallyLoading = true;
          }
          this.repairBackendData();
          this.initPmtOnCodOptions();
          this.initAlert();

          this.setShipmentServicesAllowed();
          this.refreshAllPackageDimEditable();
        }).catch((err) => {
          console.log("Error loading shipment " + this.id + " state = " + err.status, err);
          this.msg(
            (err.status && this.$t("ERROR.STATUS." + err.status) || this.$t(err.statusText)) +
            (err.body && err.body.text && (": " + err.body.text) || ""),
            "error"
          );
        }).finally(() => {
          this.validPrice = true;
          this.loading = false;
        });
      } else {
        // new shipment
        this.shipment = getDefaultShipment();
        this.initDefaults();
        this.validPrice = false;
        this.validDelivery = false;
        if (this.wmsOrder) {
          this.initWmsOrderShipment();
        }
        setTimeout(() => (this.loading = false), 500);
      }
      this.initCostCenter()
    },

    initAlert() {
      const cancelled = this.shipment.cancelled;
      const owned = this.shipment.owned;
      if (cancelled || !owned) {
        let action = "";
        action += cancelled ? this.$t("canceled") : "";
        action += !owned    ? this.$t("external") : "";
        if (cancelled && !owned)
          action = this.$t("canceled") + "/" + this.$t("external");
        this.msg(this.$t("Request.alert.readonly.start") + " " + action + " " + this.$t("Request.alert.readonly.end"), "warning", 0);
      }
    },

    initDefaults() {
      let s = null;
      try {
        s = JSON.parse(this.requestPrefs);
      } catch(e) {
        console.log("ERROR: Request preferences cannot be parsed!");
        s = null;
      }

      if (!!s && !!s.source)
      {
        s.source.after = null;
        s.source.until = null;
        // let pn = this.shipment.source.personal_number || "";
        this.shipment.source = JSON.parse(JSON.stringify(s.source));
        // this.shipment.source.personal_number = pn;
      }

      if (s && s.personalNumber && !this.shipment.source.personal_number)
        this.shipment.source.personal_number = s.personalNumber;

      {// Calendar main defaults
        this.shipmentStored = {};
        this.calendar.sourceDate = null;
      }

      let u = this.userInfo;

      if (u && u.code && u.name)
        this.initSender(u);
      else
        this.loadUserInfo().then(u => {
          if (u && u.code && u.name)
            this.initSender(u);
          if (u && u.options)
            this.applyPmtOnCodOptions(u.options, false);
        }).catch((err) => {
          console.log("Error loading user info", err);
        });

      if (u && u.options)
        this.initUserOpts(u.options);
    },

    initSender(u) {
      if (!!this.shipment.source)
        this.shipment.source.partner = { code: u.code, name: u.name };

      if (!!u.phone)
        this.shipment.source.contact_phone = u.phone;

      if (!!u.person)
        this.shipment.source.contact_person = u.person;

      if (!!u.email)
        this.shipment.source.contact_email = u.email;
    },

    initUserOpts(opts) {
      if (opts.sourceDoor !== null)
        this.shipment.source.direct = opts.sourceDoor;
      if (opts.targetDoor !== null)
        this.shipment.target.direct = opts.targetDoor;

      if (opts.sourceDoorEditable !== null && this.shipment.source.direct !== null)
        this.userOpts.sourceDoorEditable = opts.sourceDoorEditable;
      if (opts.targetDoorEditable !== null && this.shipment.target.direct !== null)
        this.userOpts.targetDoorEditable = opts.targetDoorEditable;

      if (opts.paymentMethod)
        setTimeout(() => this.shipment.payment.method = opts.paymentMethod, 500);
      if (opts.paymentType)
        setTimeout(() => this.shipment.payment.cash = opts.paymentType === "CASH", 500);
      if (opts.paymentMethodEditable !== null && this.shipment.payment.method !== null)
        this.userOpts.paymentMethodEditable = opts.paymentMethodEditable;

      this.applyPmtOnCodOptions(opts, true);

      if (opts.receiptRequired !== null)
        this.shipment.return_doc = opts.receiptRequired;
      if (opts.proofOfDelivery !== null)
        this.shipment.return_pod = opts.proofOfDelivery;

      if (opts.packageReturn)
        this.userOpts.packageReturn = opts.packageReturn;

      if (opts.shipmentService && opts.shipmentService.type)
        this.userOpts.shipmentService = opts.shipmentService;
      if (opts.cityShipmentService && opts.cityShipmentService.type)
        this.userOpts.cityShipmentService = opts.cityShipmentService;

      this.userOpts.saturdayAllowed = opts.saturdayAllowed;

      this.setShipmentServicesAllowed(opts);
      this.setServiceTypeDefaults();// maybe remove because its called from watchers

      if (opts.shipmentType) {
        this.shipmentType = opts.shipmentType === "LTL_FTL" ? "COMPLEX" : opts.shipmentType;
      }
      if (opts.shipmentTypesAllowed) {
        let allowed = [];
        opts.shipmentTypesAllowed.forEach(elem => {
          if (elem === "MIXED")
            allowed.push( "COMPLEX-NETWORK-FROM", "COMPLEX-NETWORK-TO" );
          else if (elem === "LTL_FTL")
            allowed.push("COMPLEX");
          else
            allowed.push( elem );
        });
        this.shipmentTypes = allowed;
      }

      if (opts.shipmentDescription) {
        this.shipment.description = opts.shipmentDescription;
      }
    },
    normalizePmtForContract(opts) {
      if (!opts && this.userInfo)
        opts = this.userInfo.options;
      if (!opts || opts.pmtOnCodEditable !== false
          || !this.shipment || !this.shipment.cash_on_delivery
          || (this.id > 0 && !this.$route.params.IS_CLONE
              && this.isShipmentReadonly))
        return;

      this.shipment.cash_on_delivery.postal = opts.pmtOnCod === true;
    },
    applyPmtOnCodOptions(opts, setDefault) {
      if (!opts)
        return;

      this.userOpts.pmtOnCodEditable = opts.pmtOnCodEditable !== false;
      if (setDefault && opts.pmtOnCod !== null)
        this.shipment.cash_on_delivery.postal = opts.pmtOnCod;
      this.normalizePmtForContract(opts);
    },
    initPmtOnCodOptions() {
      let u = this.userInfo;
      if (u && u.options)
        this.applyPmtOnCodOptions(u.options, false);
    },
    setPackageReturnDefaults() {
      if (this.userOpts.packageReturn === "NONE")
        this.shipment.return_pkg = false;
      else if (this.userOpts.packageReturn === "AUTO")
        this.shipment.return_pkg = true;
      else if (this.userOpts.packageReturn === "STANDARD")
        this.shipment.return_pkg = this.shipment.contents.every(elem => this.userOpts.packaging[elem.packaging.code].standard === true || this.userOpts.packaging[elem.packaging.code].small === true);
    },
    getServiceType() {
      let  service = this.isCityServiceType ? this.userOpts.cityShipmentService : this.userOpts.shipmentService;
      if (!service) {
        // fallback if there is no default contract based user services.
        let defservice = getDefaultServiceTypes().find(elem => this.isCityServiceType ? elem.cityDefault : elem.default);
        service = {
          type: defservice ? defservice.value : '',
          time: null
        }
      }
      return {...service};
    },
    setServiceTypeDefaults() {
      if (this.shipment.service.type) {
        return;
      }

      const service = this.getServiceType();
      this.shipment.service = service;

      // console.log('setServiceDefaults service=', service);
    },
    setShipmentServicesAllowed(opts) {
      if (!opts && this.userInfo && this.userInfo.options)
        opts = this.userInfo.options;

      this.userOpts.shipmentServicesAllowed.COURIER_CITY_150 = opts.courierCity150Allowed;
      this.userOpts.shipmentServicesAllowed.COURIER_CITY_EXPRESS = opts.courierCityExpressAllowed;
      this.userOpts.shipmentServicesAllowed.TARGO_ON_SITE = opts.targoOnSiteAllowed;
    },

    setServiceType() {
      // If the current service is the same type as the new one
      if (getDefaultServiceTypes().filter(elem => this.isCityServiceType ? elem.intracity : elem.intercity)
                                  .find(elem => elem.value === this.shipment.service.type)
      ) {
        return;
      }

      const service = this.getServiceType();
      if (this.shipment.service.type !== service.type) {
        this.msg( this.$t('The service type was set to', [ this.$t('ServiceType.' + service.type) ]), 'warning', 6000 );
      }
      this.shipment.service = service;

    },

    initWmsOrderShipment() {
      const ship = this.shipment;
      const src  = this.shipment.source;

      // Source Office
      this.userOpts.sourceDoorEditable = false;
      src.direct = false;
      src.office = {
        code: this.wmsOrder.tp_station_code,
        name: this.wmsOrder.tp_station_name,
        // WMS shipments originate from a Bulgarian Transpress office.
        address: { country: { code: "BG" } }
      }
      this.isShipmentSourceOfficeDisabled = true;

      // Target Address from Customer
      if (this.wmsOrder.customer) {
        ship.target = Object.assign(ship.target, {
          ...this.wmsOrder.customer
        });
      }

      // Weight SUM of products
      let weights = 0;
      this.wmsOrder.detail.forEach(elem => weights += elem.weight);
      if (!ship.contents || !ship.contents.length) {
        ship.contents.push( getDefaultShipment().contents );
      }
      ship.contents[0].weight = weights;
    },

    save() {
      if (!this.validateAll()) {
        this.msg( this.$t("Please fix errors before continuing!"), "error" );
        return;
      }

      this.loading = true;
      let data = this.sanitise();
      let params = {};
      if (this.wmsOrder) {
        params.wms_order_code = this.wmsOrder.order_code;
      }

      if (this.shipment.id === 0) {
        data.id = null;
        shipments.create(params, data).then((response) => {
          this.shipment = response.body;
          this.repairBackendData();
          this.processErrors(null);
          this.loadDisabled = true;
          this.$router.push({ params: { id: this.shipment.id }});
        }).catch((error) => {
          this.processErrors(error.body.errors);
          this.msg( this.$t("Please fix errors before continuing!"), "error" );
        }).finally(() => {
          this.validPrice = true;
          this.loading = false;
        });
      }
      else {
        params.id = data.id;
        shipments.save(params, data).then((response) => {
          this.$router.go(0);
          // this.shipment = response.body;
          // this.repairBackendData();
          // this.processErrors(null);
        }).catch((error) => {
          this.processErrors(error.body.errors);
          this.msg( this.$t("Please fix errors before continuing!"), "error" );
        }).finally(() => {
          this.validPrice = true;
          this.loading = false;
        });
      }
      this.storePreferences('costCenter');
    },

    order() {
      // required by the backend to put shipment in "request" state
      this.shipment.request = true;
      this.save();
      // clear that from the data
      delete this.shipment.request;
    },

    cancel() {
      this.showConfirm = true;
    },

    doCancel(confirm) {
      this.showConfirm = false;
      if (!confirm) return;

      if (this.loading || !this.shipment.cancellable) return;
      this.loading = true;

      shipments.cancel({ id: this.shipment.id }).then((response) => {
        this.$router.push({ name: "shipments" });
      }).catch((error) => {
        console.log("Error cancelling shipment", error);
        if (error.body && error.body.errors)
          this.processErrors(error.body.errors);
      }).finally(() => {
        this.loading = false;
      });
    },

    printLabel() {
      if (this.shipment.id === 0)
        return;
      if (this.loading)
        return;
      //
      this.loading = true;
      shipments.printLabel([ this.shipment.id ]).then(response => {
        downloadFile(response);
      }).catch(error => {
        this.msg( this.$t("Error downloading label PRN files"), "error" );
      }).finally(() => {
        this.loading = false;
      });
    },

    printMenu() {
      if (this.loading || this.shipment.id === 0)
        return;

      let usr = this.userInfo;
      if (usr && usr.options && usr.options.showPrice)
        this.printOpts.price = usr.options.showPrice;
      if (usr && usr.options && usr.options.showInsurance)
        this.printOpts.insurance = usr.options.showInsurance;

      this.printOpts.show = true;
    },

    printManifest() {
      if (this.shipment.id === 0)
        return;
      if (this.loading)
        return;
      //
      this.loading = true;
      let//
      print = {
        insurance: this.printOpts.insurance,
        price: this.printOpts.price,
        shipmentIds: [this.shipment.id]
      };
      shipments.printManifest(print).then(response => {
        downloadFile(response);
      }).catch(error => {
        this.msg( this.$t("Error downloading manifest PDF"), "error" );
      }).finally(() => {
        this.loading = false;
      });
    },

    stepComplete(idx) {
      if (idx < 3) {
        let endpoint = (idx === 1) ? this.shipment.source : this.shipment.target;
        let complete = (endpoint.partner && endpoint.partner.name && endpoint.partner.name.length > 0) && this.stepRules(idx);
        return complete;
      }
      if (idx === 3) {
        if (!this.shipment.description || this.shipment.description.length === 0)
          return false;
      }
      if (idx === 4) {
        return true;
      }
      return this.stepRules(idx);
    },

    stepRules(idx) {
      let//
      f = this.$refs.form1;
      if (idx == 2)
        f = this.$refs.form2;
      else if (idx == 3)
        f = this.$refs.form3;
      else if (idx == 4)
        f = this.$refs.form4;

      if (!f)
      {
        console.log("ERROR: stepRule " + idx + " form not found! Cannot validate it!");
        return true;
      }

      let hasErr = false;
      this.errorConsumed.forEach((v, k) => {
        if (this.errorMessages.has(k) && (idx === v)) hasErr = true;
      });
      if (idx === 3 && this.shipment.contents.some(parcel => hasAdr(parcel) && !this.adrAllowed(parcel))) hasErr = true;
      let stepRes = (hasErr) ? false : f.validate();
      return stepRes;
    },

    checkConnectedShipments(c) {
      // make sure POD & CMR options are not checked together
   //   if (c === 'return_pod' && this.shipment[c] === true){
   //     this.shipment.return_cmr = false;
   //   }
   //   else if (c === 'return_cmr' && this.shipment[c] === true){
   //     this.shipment.return_pod = false;
   //   }
    },

    outlinePackage( idx ) {
      return this.enablePackageRemoval && this.shipment.contents[idx].count < 1;
    },

    removePackage( idx ) {
      this.shipment.contents.splice(idx, 1);
      this.setPackageReturnDefaults();
    },

    packageCountChanged(idx) {
      if (this.enablePackageRemoval && this.shipment.contents[idx].count < 1) {
        this.shipment.contents[idx].packageRemovalTimerId = window.setTimeout(() => {
          this.removePackage( idx );
        }, 2000);
      } else if (this.shipment.contents[idx].count > 0 && typeof this.shipment.contents[idx].packageRemovalTimerId === 'number') {
        window.clearTimeout(this.shipment.contents[idx].packageRemovalTimerId);
        this.shipment.contents[idx].packageRemovalTimerId = null;
      }
    },

    refreshAllPackageDimEditable(){
      // No need to update new/template shipments
      if (this.shipment.id === 0)
        return;
      // shipment must have an array of package contents
      if (!Array.isArray(this.shipment.contents) || !(this.shipment.contents.length > 0))
        return;
      // update for each package
      for (let i = 0; i < this.shipment.contents.length; i++)
        this.refreshPackageDimEditable(i);
    },

    refreshPackageDimEditable(idx){
      let//
      e = Array.isArray(this.shipment.contents) && idx < this.shipment.contents.length ? this.shipment.contents[idx].packaging : {};
      if (!!e.code){
        e = this.userOpts.packaging[e.code];
        if (!!e && !!e.code) {
          this.shipment.contents[idx].dimXEditable = !(typeof e.size_x === 'number');
          this.shipment.contents[idx].dimYEditable = !(typeof e.size_y === 'number');
          this.shipment.contents[idx].dimZEditable = !(typeof e.size_z === 'number');
        }
      }
    },

    packageChanged(e, idx) {
      if (!e)
        return;

      this.refreshPackageDimEditable(idx);

      if (this.shipment.contents[idx].dimXEditable) {
        this.shipment.contents[idx].parcel_x = 0;
      } else {
        this.shipment.contents[idx].parcel_x = e.size_x;
      }
      if (this.shipment.contents[idx].dimYEditable) {
        this.shipment.contents[idx].parcel_y = 0;
      } else {
        this.shipment.contents[idx].parcel_y = e.size_y;
      }
      if (this.shipment.contents[idx].dimZEditable) {
        this.shipment.contents[idx].parcel_z = 0;
      } else {
        this.shipment.contents[idx].parcel_z = e.size_z;
      }

      this.setPackageReturnDefaults();
    },

    addPackage() {
      let pkg = {
        "id" : null,
        "count": 1,
        "packaging": {
          "code": null,
          "name": ""
        },
        "weight": 0,
        "volume": 0,
        "lademeters": 0,
        "single_layer": false,
        "dimXEditable": true,
        "dimYEditable": true,
        "dimZEditable": true,
        "parcel_x": 0,
        "parcel_y": 0,
        "parcel_z": 0
      };
      let ps = this.shipment.contents;
      if (ps && ps.length > 0 ) {
        // copy the last packaging
        let lastPkg = ps[ps.length - 1];
        pkg.packaging = JSON.parse(JSON.stringify(lastPkg.packaging));
        pkg.parcel_x = lastPkg.parcel_x;
        pkg.parcel_y = lastPkg.parcel_y;
        pkg.parcel_z = lastPkg.parcel_z;
      }
      this.shipment.contents.push(pkg);
      this.setPackageReturnDefaults();
    },

    storePreferences(what) {
      let s = null;
      try {
        s = JSON.parse(this.requestPrefs);
      } catch(e) {
        console.log("ERROR: Request preferences cannot be parsed!");
        s = {};
      }

      if (what === "priceBox")
        s.priceBox = this.priceBox;
      else
      if (what === "screen") {
        if (!s.screen)
          s.screen = {};
        s.screen.screen  = this.screen;
        s.screen.expanel = this.screenModesData.expanel;
      }
      else
      if (what === "source" && this.shipment.source)
        s.source = this.shipment.source;
      else
      if (what === "personalNumber") {
        s.personalNumber = this.shipment.source.personal_number;
      }
      else
      if (what === "contactSuggestOwn" && this.contactSuggestOwn)
        s.contactSuggestOwn = this.contactSuggestOwn;
      else
      if (what === "costCenter" && this.shipment.cost_center)
        s.costCenter = this.shipment.cost_center;

      this.updateRequestPrefs(JSON.stringify(s));
    },

    processContact() {// Convert contact fields from shipment to contact format
      let target = this.shipment.target;
      this.contact.data = {
        partner : target.partner,
        address : target.address,
        name    : target.contact_person,
        phone   : target.contact_phone,
        email   : target.contact_email
      };
      this.contact.show = true;
    },
    processContactBack(data) {
      if (data && typeof data === "object") {// Convert back
        let target = this.shipment.target;
        target.partner        = data.partner;
        target.address        = data.address;
        target.contact_person = data.name;
        target.contact_phone  = data.phone;
        target.contact_email  = data.email;
      }
      this.contact.show = false;
    },

    personalNumberSet() {
      if (!this.shipment.source.personal_number) {
        this.shipment.source.personal_number = "";
      }
      setTimeout(() => document.querySelector("#personalNumber").focus(), 500);
    },

    stationRequest(settlementCode, who) {
      if (!settlementCode)
        return;

      if (!this.isComplexShipment)
        return;

      let settlement_code = settlementCode,
          result = {};

      suggest.suggestStation({settlement_code}).then((response) => {
        result = response.body;

        // this.shipment[who].station = result;// In order to trigger changes in VOfficeSuggest component, the EXACT v-model object (shipment.source|target) must be changed, not any of its children. So it is easier to change station object, because it's much smaller. AND there is additional fields necessary: disabled and distance! [2019-10-24]

        let stat = this.station[who];
        this.station[who] = {
          disabled: stat.disabled,
          value: result,
        };
      }).catch((err) => {
        console.log(err);
      }).finally(() => {
      });
    },
    isSameSettlementAsStored(val, sot) {// sot = sourceOrTarget
      let prefs = null;
      try {
        prefs = JSON.parse(this.requestPrefs);
      } catch(e) {
        console.log("ERROR: Request preferences cannot be parsed (used by settlement.code)!");
      }
      return (!!prefs && !!prefs[sot] && !!prefs[sot].address && !!prefs[sot].address.settlement && val == prefs[sot].address.settlement.code);
    },

    priceFail(description) {
      this.shipment.payment = getDefaultShipment().payment;
      this.msg(
        description ? description : this.$t("Price cannot be calculated due to incorrect parameters or settings"),
        "warning",
        0 );
    },

    /** Returns or sets calculated model value run-time. If `item` exists then fn called to set (calculated) value to model (`@input`); otherwise returns calculated model to `:value` */
    parcelDimsCalc(val, fieldname, item) {
      let ratio = 0;
      let unit = this.prefs.units.parcelDims.find(elem => elem.id === this.units.parcelDims);
      if (unit && unit.ratio)
        ratio = unit.ratio;

      //val = Number(val);// no floats in FF

      if (item) {// @input="model=
        item[fieldname] = ratio
          ? Math.round(((val * 0.01) + Number.EPSILON) * 10000) / 10000
          : val;
      }
      else {// :value="
        return ratio
          ? Math.round(((val / 0.01) + Number.EPSILON) * 100) / 100
          : val;
      }
    },

    loadPackagings() {
      shipments.packagings({}, {
          headers: { "Accept-Language": this.globalConfig.lang }
      }).then((res) => {
          this.packList = res.body.result;
          if (Array.isArray(this.packList))
            this.packList.forEach(e => this.$set(this.userOpts.packaging, e.code, {...e}));
          this.refreshAllPackageDimEditable();
      }).catch((err) => {
          console.log("Error loading packagings, status = " + err.status, err);
      });
    },

    msg(text, type, timeout) {
      this.msgText = text ? text : "";
      this.msgType = type ? type : "info";
      this.msgTimeout = 3000;
      if (typeof timeout !== "undefined")
        this.msgTimeout = timeout;
      else if (type === "error")
        this.msgTimeout = 6000//0;
      this.showMsg = true;
    },
    validDimension(val, packCode) {
      if (this.userOpts.packaging[packCode] && this.userOpts.packaging[packCode].standard) {
        return val > 0 && val <= 2.4;
      } else {
        return val >= 0
      }
    },
    calculateVolume(item) {
      if (!item || !item.packaging)
        return;

      let dimX = item.parcel_x;
      let dimY = item.parcel_y;
      let dimZ = item.parcel_z;
      let count = item.count;

      let packagingCode = item.packaging.code;
      let volume = 0;
      if (count && this.validDimension(dimX, packagingCode) && this.validDimension(dimY, packagingCode) && this.validDimension(dimZ, packagingCode))
        volume = count * dimX * dimY * dimZ;

      return volume;
    },
    calcParcelWeight(item) {
      if (!item || !item.weight || item.weight === 0)
        return 0;

      if (!item.count || item.count <= 0)
        return 0;

      return Number(item.weight / item.count).toFixed(2);
    },
    isVRequiredAmazon() {
     return (
      this.showAmazonReference
      && !!this.shipment.target.address
      && !!this.shipment.target.address.country
      && this.shipment.target.address.country.code !== 'BG'
      ) || !!this.shipment.amazon_reference;
    },
    initCostCenter () {
      let info = this.userInfo;
      if (!info || !info.costCenter)
        this.loadUserInfo().then(u => {
          info = u
        }).catch((err) => {
          console.log("Error loading user info", err);
        });

      if (info && info.costCenters && info.costCenters.length > 0)
        this.costCenters = info.costCenters

      if (this.costCenters.length > 0 && !this.getSavedCostCenter()) {
        this.shipment.cost_center = this.costCenters[0];
      } else {
        this.shipment.cost_center = getSavedCostCenter();
      }
    },
    getSavedCostCenter() {
      try {
        const prefs = JSON.parse(this.requestPrefs || '{}');
        return prefs.costCenter || null;
      } catch(e) {
        console.log("ERROR: Request preferences cannot be parsed!");
        return null;
      }
    }
  },
  created() {
    this.resizePriceBox = debounce( this.resizePriceBoxImpl, 1000 );
    this.invalidatePrice = debounce( this.invalidatePriceImpl, 1000 );
  },

  mounted() {
    let myPrefs = null;
    try {
      myPrefs = JSON.parse(this.requestPrefs);
    } catch(e) {
      console.log("ERROR: Request preferences cannot be parsed!");
      myPrefs = null;
    }

    this.screen = this.screenModes[0];

    if (myPrefs)
    {
      if (myPrefs.priceBox)
        this.priceBox = JSON.parse(JSON.stringify(myPrefs.priceBox));
      if (myPrefs.contactSuggestOwn)
        this.contactSuggestOwn = JSON.parse(JSON.stringify(myPrefs.contactSuggestOwn));
      if (myPrefs.screen) {
        this.screenModesData.expanel = myPrefs.screen.expanel;
        if (myPrefs.screen.screen)
          this.screen = myPrefs.screen.screen;
      }
      if (myPrefs.units) {
        Object.keys(myPrefs.units).forEach(key => {
          this.units[key] = myPrefs.units[key];
        });
      }
    }

    this.loadPackagings();
    this.load();
    this.resizePriceBox();

    window.addEventListener("load", this.initScreenModes());
  },
  destroyed() {
    window.removeEventListener("load", this.initScreenModes());
  },
}
</script>

<style>
.disabled.v-input input {
  color: rgba(0, 0, 0, .5) !important;
}
.manifest-field input {
  cursor: pointer;
}
.blink {
  animation: blink 1s step-start infinite;
}
@keyframes blink {
  50% {
    opacity: 0;
  }
}
.v-card__actions .actions-bar-mobile .v-btn + .v-btn {
  margin-left: 0;
}
.v-card__actions .actions-bar-mobile.is-floating {
  margin-bottom: 80px;
}
#pricesActivator:hover,
#pricesFloatActivator:hover {
  cursor: pointer;
}

.v-stepper:not(.v-stepper--vertical) .v-stepper__label {
  display: flex;
}
.v-stepper.sm:not(.v-stepper--vertical) .v-stepper__step__step {
  margin-right: 8px;
}
.v-stepper--alt-labels .v-stepper__step {
  flex-basis: auto;
  padding-left: 12px;
  padding-right: 12px;
}
.v-stepper--alt-labels .v-stepper__step__step {
  margin-bottom: 0;
}
.v-stepper--alt-labels .v-stepper__header .v-divider {
  align-self: center;
  margin: 0 -16px;
}

.v-list >>> .v-list--disabled {
	color: rgba(0,0,0,.15);
}

.readonly-overlay:after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: #fff5;
}

.price-row-group {
  border-bottom: solid 1px rgba(0,0,0,.15);
}
.v-list.price-row > div:nth-child(odd) {
  background-color: rgba(0,0,0,.08);
}
</style>
